import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { ClipboardList, CloudSun, Users, Package, AlertTriangle, StickyNote, Camera } from 'lucide-react';
import Icon from '../../components/AppIcon';
import SectionCard from '../../components/SectionCard';
import StatsCard from '../../components/ui/StatsCard';
import useTranslation from '../../hooks/useTranslation';
import { useAuth } from '../../auth/AuthProvider';
import { projectService } from '../../services/projectService';
import { getByProject, create, update, deletePhoto, updatePhotoCaptions, canView, canEdit, canApprove, approve, unapprove, addTask, updateTask, deleteTask, getById, getPreviousLogWithCrew } from '../../services/dailyLogService';
import { canSeeAllProjects, canEditProject } from '../../utils/permissions';
import { getImageDimensions } from '../../utils/imageCompression';
import { supabase } from '../../lib/supabase';
import PortalDropdown from '../../components/ui/PortalDropdown';
import { SPACING, COLORS, jsPdf as jsPdfHelpers, fetchOrgLogoAsBase64 } from '../../pdf/designSystem';
import ValidationModal from '../../components/ValidationModal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { statusVariant } from '../../utils/statusVariant';
import Badge from '../../components/ui/Badge';
import BulkActionBar from '../../components/ui/BulkActionBar';

// ─── Module-level org logo cache (session-scoped, resets on page refresh) ─────
let _cachedOrgLogo = null;
let _cachedOrgLogoPath = null;

const getOrgLogoBase64Cached = async (supabaseClient, logoPath) => {
  if (_cachedOrgLogo && _cachedOrgLogoPath === logoPath) return _cachedOrgLogo;
  _cachedOrgLogo = await fetchOrgLogoAsBase64(supabaseClient, logoPath);
  _cachedOrgLogoPath = logoPath;
  return _cachedOrgLogo;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateStr, locale) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d?.toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

const formatDateShort = (dateStr, lang) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d?.toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatDateTime = (isoStr) => {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  return d?.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// « Aujourd'hui » = la date de Montreal, pas la date UTC : apres 20 h, la date
// UTC est deja celle du lendemain, et un journal ne peut pas etre date du futur.
// en-CA ecrit AAAA-MM-JJ.
const todayStr = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Toronto' })?.format(new Date());

// Jours ouvrables (lundi a vendredi) sans journal sur les 30 derniers jours,
// aujourd'hui compris, du plus recent au plus ancien. Le calcul se fait sur
// des dates AAAA-MM-JJ, a midi UTC, pour ne dependre d'aucun fuseau.
const missingWeekdays = (logDates, today, days = 30) => {
  const have = new Set(logDates);
  const out = [];
  const cursor = new Date(`${today}T12:00:00Z`);
  for (let i = 0; i < days; i++) {
    const dow = cursor?.getUTCDay();
    const dateStr = cursor?.toISOString()?.split('T')?.[0];
    if (dow !== 0 && dow !== 6 && !have?.has(dateStr)) out?.push(dateStr);
    cursor?.setUTCDate(cursor?.getUTCDate() - 1);
  }
  return out;
};

// Equipe pre-remplie d'un nouveau journal : les sous-traitants du journal
// precedent qui ont encore au moins une tache non terminee, avec ces taches.
// Chaque tache garde le lien vers sa tache d'origine (previous_task_id).
const carriedCrewFrom = (prevLog) =>
  (prevLog?.crew || [])
    ?.map((c) => {
      const incompleteTasks = (c?.tasks || [])?.filter((t) => !t?.completed);
      if (incompleteTasks?.length === 0) return null;
      return {
        id: null,
        subcontractorId: c?.subcontractor_id,
        subcontractor: c?.subcontractor,
        workerCount: c?.worker_count ?? '',
        notes: '',
        tasks: incompleteTasks?.map((t) => ({
          id: null,
          nom: t?.nom,
          pourcentage: t?.pourcentage ?? 0,
          previous_task_id: t?.id,
          sort_order: t?.sort_order ?? 0,
          _minPercent: t?.pourcentage ?? 0,
        })),
      };
    })
    ?.filter(Boolean);

const WEATHER_CONDITIONS = ['sunny', 'cloudy', 'rain', 'snow', 'storm', 'other'];

const WEATHER_ICONS = {
  sunny: '☀',
  cloudy: '☁',
  rain: '☔',
  snow: '❄',
  storm: '⛈',
  other: '—',
};

// Libelles des types d'enjeu — TABLE UNIQUEMENT. La modale garde son propre
// <select>. Un type inconnu (donnee heritee) retombe sur sa valeur brute.
const ISSUE_TYPE_LABELS = {
  incident: { fr: 'Incident', en: 'Incident' },
  retard:   { fr: 'Retard',   en: 'Delay' },
  qualite:  { fr: 'Qualité',  en: 'Quality' },
  securite: { fr: 'Sécurité', en: 'Safety' },
  autre:    { fr: 'Autre',    en: 'Other' },
};

// ─── PDF Generator ────────────────────────────────────────────────────────────

const fetchPhotoAsBase64 = async (signedUrl) => {
  try {
    const response = await fetch(signedUrl);
    if (!response?.ok) return null;
    const blob = await response?.blob();
    const b64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    // Also get natural dimensions for aspect-ratio-aware PDF rendering
    let width = 4;
    let height = 3;
    try {
      const dims = await getImageDimensions(blob);
      width = dims?.width;
      height = dims?.height;
    } catch {
      // fallback 4:3
    }
    return { b64, width, height };
  } catch (err) {
    console.warn('Photo fetch failed', err);
    return null;
  }
};

// Concurrency-limited batch fetcher for PDF photo pre-fetch
const fetchPhotosInBatches = async (photos, batchSize = 4) => {
  const results = new Array(photos.length)?.fill(null);
  for (let i = 0; i < photos?.length; i += batchSize) {
    const batch = photos?.slice(i, i + batchSize);
    const batchResults = await Promise.all(
      batch?.map((p, idx) =>
        p?.signedUrl
          ? fetchPhotoAsBase64(p?.signedUrl)?.then(data => ({ idx: i + idx, data }))
          : Promise.resolve({ idx: i + idx, data: null })
      )
    );
    for (const { idx, data } of batchResults) {
      results[idx] = data;
    }
  }
  return results;
};

const generateDailyLogPDF = async (log, project, t, lang = 'en', logoBase64 = null, orgName = 'OnsitePM') => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = 210;
  const margin = 20;
  const contentW = pageW - margin * 2;
  let y = 0;

  // ── Pre-fetch photos as base64 (concurrency-4 batches) ──
  const photos = log?.daily_log_photos || [];
  const photoResults = await fetchPhotosInBatches(photos, 4);
  const validPhotos = photoResults
    ?.map((result, i) => result ? ({ b64: result?.b64, width: result?.width, height: result?.height, caption: photos?.[i]?.caption }) : null)
    ?.filter(Boolean);

  // ── Header (shared design system) ──
  // TASK 3c: Override title font size locally (shared FONTS.title.size=22 unchanged)
  const LOCAL_TITLE_FONT_SIZE = 17;
  // TASK 3b: Override logo size locally (shared logoMaxW=40/logoMaxH=15 in jspdfHelpers unchanged)
  const LOCAL_LOGO_MAX_W = 50;
  const LOCAL_LOGO_MAX_H = 18;

  const docTitle = lang === 'fr' ? 'JOURNAL DE CHANTIER' : 'DAILY LOG';
  const approvalStatus = log?.approval_status || 'submitted';
  const statusLabel = approvalStatus === 'approved'
    ? (lang === 'fr' ? 'Approuvé' : 'Approved')
    : (lang === 'fr' ? 'En attente' : 'Pending');

  // ── Inline header render (local overrides for logo size + title font size) ──
  // We render the header manually here instead of calling jsPdf.renderHeader
  // so we can apply LOCAL_LOGO_MAX_W, LOCAL_LOGO_MAX_H, LOCAL_TITLE_FONT_SIZE
  // without touching the shared jspdfHelpers constants.
  const headerMargin = SPACING?.pageMargin;
  const pageWDoc = doc?.internal?.pageSize?.getWidth();
  const cwDoc = pageWDoc - headerMargin * 2;
  const rightX = headerMargin + cwDoc;
  let headerCurY = headerMargin;

  // Logo (left side, local overrides)
  let logoEndX = headerMargin;
  if (logoBase64) {
    try {
      const props = doc?.getImageProperties(logoBase64);
      const naturalRatio = props?.width / props?.height;
      let w = LOCAL_LOGO_MAX_W;
      let h = w / naturalRatio;
      if (h > LOCAL_LOGO_MAX_H) {
        h = LOCAL_LOGO_MAX_H;
        w = h * naturalRatio;
      }
      const logoX = headerMargin;
      const logoYCentered = headerMargin + (LOCAL_LOGO_MAX_H - h) / 2;
      const m = logoBase64?.match(/^data:image\/(\w+);base64,/);
      let fmt = m ? m?.[1]?.toUpperCase() : 'PNG';
      if (fmt === 'JPG') fmt = 'JPEG';
      doc?.addImage(logoBase64, fmt, logoX, logoYCentered, w, h, undefined, 'FAST');
      logoEndX = headerMargin + w;
    } catch (_) {
      // logo failed — skip silently
    }
  }

  // Title (right-aligned, local font size override)
  doc?.setFont('helvetica', 'bold');
  doc?.setFontSize(LOCAL_TITLE_FONT_SIZE);
  doc?.setTextColor(31, 41, 55); // COLORS.black
  doc?.text(docTitle?.toUpperCase(), rightX, headerCurY + 7, { align: 'right' });
  headerCurY += 10;

  // Meta lines (right-aligned) — TASK 3a: measure value width in bold to prevent overlap
  const metaLines = [
    { label: lang === 'fr' ? 'Projet' : 'Project', value: `${project?.projectNumber || ''} · ${project?.name || ''}` },
    { label: 'Date', value: formatDate(log?.log_date, lang) },
    { label: lang === 'fr' ? 'Statut' : 'Status', value: statusLabel },
  ];
  metaLines?.forEach(({ label, value }) => {
    // Measure label width in normal font
    doc?.setFont('helvetica', 'normal');
    doc?.setFontSize(7.5);
    doc?.setTextColor(113, 113, 122); // gray500
    const labelText = `${label}: `;
    const labelW = doc?.getTextWidth(labelText);

    // Measure value width in BOLD font (same font as render) — TASK 3a fix
    doc?.setFont('helvetica', 'bold');
    doc?.setFontSize(7.5);
    const valueW = doc?.getTextWidth(String(value ?? ''));

    // Render label right-aligned, then value right-aligned immediately to its left
    // Both anchored to rightX with no overlap
    doc?.setFont('helvetica', 'normal');
    doc?.setFontSize(7.5);
    doc?.setTextColor(113, 113, 122);
    doc?.text(labelText, rightX - valueW, headerCurY, { align: 'right' });

    doc?.setFont('helvetica', 'bold');
    doc?.setTextColor(31, 41, 55); // black
    doc?.text(String(value ?? ''), rightX, headerCurY, { align: 'right' });

    headerCurY += 4.5;
  });

  // Divider line
  const headerBottom = Math.max(headerCurY + 4, headerMargin + SPACING?.headerHeight);
  doc?.setLineWidth(0.4);
  doc?.setDrawColor(31, 41, 55);
  doc?.line(headerMargin, headerBottom, headerMargin + cwDoc, headerBottom);

  const headerBottomY = headerBottom + 4;

  let currentY = headerBottomY + SPACING?.sectionGap;

  const sectionHeader = (label) => {
    currentY = jsPdfHelpers?.renderSectionTitle(doc, label, currentY);
  };

  const bodyText = (text, indent = 0) => {
    doc?.setFont('helvetica', 'normal');
    doc?.setFontSize(10);
    doc?.setTextColor(55, 65, 81);
    const lines = doc?.splitTextToSize(text || '', contentW - indent);
    doc?.text(lines, margin + indent, currentY);
    currentY += lines?.length * 5 + 3;
  };

  // ── Section 1: Weather ──
  if (log?.weather_condition || log?.weather_temperature != null) {
    sectionHeader(t('dailyLog.view.weather'));
    const weatherIcon = WEATHER_ICONS?.[log?.weather_condition] || '';
    const weatherLabel = log?.weather_condition ? t(`dailyLog.weather.${log?.weather_condition}`) : '';
    const tempStr = log?.weather_temperature != null ? ` · ${log?.weather_temperature}°C` : '';
    bodyText(`${weatherIcon} ${weatherLabel}${tempStr}`);
    currentY += 2;
  }

  // ── Section 2: Crew ──
  const crew = log?.crew || [];
  const tradesCount = crew?.length;
  const workersSum = crew?.reduce((s, c) => s + (c?.worker_count || 0), 0);

  const pageHeight = doc?.internal?.pageSize?.getHeight();
  const bottomMargin = 25;

  if (crew?.length > 0) {
    sectionHeader(lang === 'fr' ? 'ÉQUIPE SUR SITE' : 'CREW ON SITE');

    // Helper: hex → rgb array
    const hexRgb = (hex) => {
      const c = hex?.replace('#', '');
      return [parseInt(c?.substring(0, 2), 16), parseInt(c?.substring(2, 4), 16), parseInt(c?.substring(4, 6), 16)];
    };

    const cardPadding = 5;
    const cardMarginBottom = 6;
    const headerH = 8;
    const taskRowH = 6;
    const notesExtraH = 5;
    const barW = 30;
    const barH = 4;

    crew?.forEach((crewRow) => {
      const tasks = (crewRow?.tasks || [])?.slice()?.sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0));
      const hasNotes = !!(crewRow?.notes?.trim());
      const hasTasks = tasks?.length > 0;

      // Estimate card height for page break check
      const estimatedH =
        headerH +
        cardPadding +
        (hasNotes ? notesExtraH : 0) +
        (hasTasks ? tasks?.length * taskRowH : taskRowH) +
        cardPadding;

      if (currentY + estimatedH > pageHeight - bottomMargin) {
        doc?.addPage();
        currentY = margin + 10;
      }

      const cardX = margin;
      const cardW = contentW;
      const cardStartY = currentY; // track card top for border

      // ── Card header band ──
      doc?.setFillColor(...hexRgb(COLORS?.gray200));
      doc?.rect(cardX, currentY, cardW, headerH, 'F');

      // Left: company name bold
      doc?.setFont('helvetica', 'bold');
      doc?.setFontSize(10);
      doc?.setTextColor(...hexRgb(COLORS?.gray900));
      const companyName = crewRow?.subcontractor?.company_name || '—';
      doc?.text(companyName?.slice(0, 38), cardX + cardPadding, currentY + 5.5);

      // Middle: trade badge (orange pill text)
      const trade = crewRow?.subcontractor?.trade || '';
      if (trade) {
        const companyNameW = doc?.getTextWidth(companyName?.slice(0, 38));
        const tradeBadgeX = cardX + cardPadding + companyNameW + 4;
        doc?.setFont('helvetica', 'bold');
        doc?.setFontSize(7.5);
        doc?.setTextColor(...hexRgb(COLORS?.orange));
        doc?.text(trade?.toUpperCase(), tradeBadgeX, currentY + 5.5);
      }

      // Right: worker count
      const workerLabel = `${crewRow?.worker_count || 0} ${lang === 'fr' ? 'ouvrier(s)' : 'worker(s)'}`;
      doc?.setFont('helvetica', 'normal');
      doc?.setFontSize(8.5);
      doc?.setTextColor(...hexRgb(COLORS?.gray700));
      doc?.text(workerLabel, cardX + cardW - cardPadding, currentY + 5.5, { align: 'right' });

      currentY += headerH;

      // ── Card body ──
      let bodyY = currentY + cardPadding;

      // Notes (italic gray)
      if (hasNotes) {
        doc?.setFont('helvetica', 'italic');
        doc?.setFontSize(8);
        doc?.setTextColor(...hexRgb(COLORS?.gray500));
        const noteLines = doc?.splitTextToSize(crewRow?.notes, cardW - cardPadding * 2);
        doc?.text(noteLines, cardX + cardPadding, bodyY);
        bodyY += noteLines?.length * 4 + 2;
      }

      // Tasks list
      if (hasTasks) {
        tasks?.forEach((task) => {
          const taskName = task?.nom || '';
          const pct = Math.min(100, Math.max(0, task?.pourcentage ?? 0));

          // Task name
          doc?.setFont('helvetica', 'normal');
          doc?.setFontSize(9);
          doc?.setTextColor(...hexRgb(COLORS?.gray900));
          const maxTaskNameW = cardW - cardPadding * 2 - barW - 14;
          const truncatedName = doc?.splitTextToSize(taskName, maxTaskNameW)?.[0] || taskName;
          doc?.text(truncatedName, cardX + cardPadding, bodyY + 3);

          // Progress bar background (gray-200)
          const barX = cardX + cardW - cardPadding - barW - 10;
          const barY = bodyY;
          doc?.setFillColor(...hexRgb(COLORS?.gray200));
          doc?.roundedRect(barX, barY, barW, barH, 1, 1, 'F');

          // Progress bar fill (orange)
          if (pct > 0) {
            const fillW = (pct / 100) * barW;
            doc?.setFillColor(...hexRgb(COLORS?.orange));
            doc?.roundedRect(barX, barY, fillW, barH, 1, 1, 'F');
          }

          // Percentage label to the right of bar
          doc?.setFont('helvetica', 'bold');
          doc?.setFontSize(8);
          doc?.setTextColor(...hexRgb(COLORS?.gray900));
          doc?.text(`${pct}%`, barX + barW + 2, bodyY + 3);

          bodyY += taskRowH;
        });
      } else {
        // No tasks empty state
        doc?.setFont('helvetica', 'italic');
        doc?.setFontSize(8);
        doc?.setTextColor(...hexRgb(COLORS?.gray500));
        const noTasksLabel = lang === 'fr' ? 'Aucune tâche enregistrée' : 'No tasks recorded';
        doc?.text(noTasksLabel, cardX + cardPadding, bodyY + 3);
        bodyY += taskRowH;
      }

      currentY = bodyY + cardPadding;

      // Card border (drawn around the full card)
      doc?.setDrawColor(...hexRgb(COLORS?.gray300));
      doc?.setLineWidth(0.3);
      doc?.rect(cardX, cardStartY, cardW, currentY - cardStartY, 'S');

      currentY += cardMarginBottom;
    });

    // ── Summary total line ──
    doc?.setFont('helvetica', 'bold');
    doc?.setFontSize(9);
    doc?.setTextColor(...hexRgb(COLORS?.gray700));
    const totalStr = t('dailyLog.view.crewTotal')?.replace('{trades}', tradesCount)?.replace('{workers}', workersSum);
    doc?.text(totalStr, margin + 2, currentY);
    currentY += 8;

  } else {
    // ── Empty state ──
    sectionHeader(lang === 'fr' ? 'ÉQUIPE SUR SITE' : 'CREW ON SITE');
    doc?.setFont('helvetica', 'italic');
    doc?.setFontSize(9);
    doc?.setTextColor(113, 113, 122); // gray500
    const emptyLabel = lang === 'fr' ? 'Aucun sous-traitant sur site ce jour-là' : 'No subcontractors on site that day';
    doc?.text(emptyLabel, margin + 2, currentY);
    currentY += 8;
  }

  // ── TASK 2: Single "NOTES" section (replaces separate "TRAVAUX EFFECTUÉS" + "NOTES") ──
  // Fallback logic: show notes first, then work_performed if present, separated by blank line.
  // If both empty → skip the section entirely.
  const notesContent = log?.notes?.trim() || '';
  const workPerformedContent = log?.work_performed?.trim() || '';
  if (notesContent || workPerformedContent) {
    sectionHeader(lang === 'fr' ? 'NOTES' : 'NOTES');
    if (notesContent) {
      bodyText(notesContent);
    }
    if (workPerformedContent) {
      if (notesContent) {
        // blank line separator between the two blocks
        currentY += 3;
      }
      bodyText(workPerformedContent);
    }
    currentY += 2;
  }

  // ── Créé par (body line — kept as-is per spec) ──
  const creatorName = log?.creator?.full_name || log?.creator?.email || '—';
  const createdStr = t('dailyLog.view.createdBy')?.replace('{author}', creatorName)?.replace('{date}', formatDateTime(log?.created_at));
  doc?.setFont('helvetica', 'normal');
  doc?.setFontSize(8);
  doc?.setTextColor(107, 114, 128);
  doc?.text(createdStr, margin, currentY);
  currentY += 5;

  const isEdited = log?.updated_at && log?.created_at && log?.updated_at !== log?.created_at;
  if (isEdited) {
    const editorName = log?.updater?.full_name || log?.updater?.email || creatorName;
    const editedStr = t('dailyLog.view.editedBy')?.replace('{editor}', editorName)?.replace('{date}', formatDateTime(log?.updated_at));
    doc?.text(editedStr, margin, currentY);
    currentY += 5;
  }

  currentY += 3;

  // ── Section 5: Photos ──
  if (validPhotos?.length > 0) {
    sectionHeader(lang === 'fr' ? 'PHOTOS' : 'PHOTOS');

    const marginLeft = margin;
    const cols = 3;
    const gap = 5;
    const usableWidth = pageW - (2 * marginLeft) - (gap * (cols - 1));
    const imgWidth = usableWidth / cols;
    const captionSpace = 6;
    const pageBottomMargin = 25;

    // Split photos into rows of `cols`
    const rows = [];
    for (let i = 0; i < validPhotos?.length; i += cols) {
      rows?.push(validPhotos?.slice(i, i + cols));
    }

    let photoY = currentY;

    for (const row of rows) {
      // Compute each cell's natural height at imgWidth, capped at 1.6× width
      const cellHeights = row?.map((photo) => {
        const naturalH = photo?.width > 0
          ? imgWidth * (photo?.height / photo?.width)
          : imgWidth * 0.75; // fallback 4:3
        return Math.min(naturalH, imgWidth * 1.6);
      });
      const rowH = Math.max(...cellHeights);
      const rowHeight = rowH + captionSpace + gap;

      // Page break check per row
      if (photoY + rowHeight > pageHeight - pageBottomMargin) {
        doc?.addPage();
        photoY = 20;
      }

      row?.forEach((photo, colIdx) => {
        const x = marginLeft + colIdx * (imgWidth + gap);
        const cellH = cellHeights?.[colIdx];

        try {
          doc?.addImage(photo?.b64, 'JPEG', x, photoY, imgWidth, cellH);
        } catch (err) {
          console.warn('addImage failed for photo', err);
        }

        // Optional caption below image (top-aligned within row)
        if (photo?.caption) {
          doc?.setFontSize(7);
          doc?.setTextColor(80, 80, 80);
          const truncated = photo?.caption?.length > 40
            ? photo?.caption?.substring(0, 37) + '...'
            : photo?.caption;
          doc?.text(truncated, x, photoY + cellH + 3);
        }
      });

      photoY += rowHeight;
    }

    currentY = photoY; // advance cursor
  }

  // ── Footer on every page (shared design system) ──
  const totalPages = doc?.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc?.setPage(i);
    jsPdfHelpers?.renderFooter(doc, {
      orgName,
      lang,
      pageNum: i,
      totalPages,
    });
  }

  return doc;
};

const downloadSinglePDF = async (log, project, t, lang, logoBase64, orgName) => {
  const doc = await generateDailyLogPDF(log, project, t, lang, logoBase64, orgName);
  const projectNum = project?.projectNumber || 'LOG';
  const dateStr = log?.log_date || 'unknown';
  doc?.save(`daily-log-${projectNum}-${dateStr}.pdf`);
};

// ─── Toast ─────────────────────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }) => (
  <div className={`fixed bottom-6 right-6 z-[200] flex items-center gap-3 px-5 py-3.5 rounded-lg shadow-lg text-white text-sm font-medium transition-all ${type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
    <Icon name={type === 'error' ? 'AlertCircle' : 'CheckCircle2'} size={18} />
    <span>{message}</span>
    <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100">
      <Icon name="X" size={16} />
    </button>
  </div>
);

// ─── Weather Pill ──────────────────────────────────────────────────────────────

const WeatherPill = ({ condition, temperature, t }) => {
  if (!condition && temperature == null) return null;
  const iconMap = {
    sunny: 'Sun', cloudy: 'Cloud', rain: 'CloudRain', snow: 'Snowflake', storm: 'CloudLightning', other: 'Thermometer'
  };
  const icon = iconMap?.[condition] || 'Thermometer';
  const label = condition ? t(`dailyLog.weather.${condition}`) : '';
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
      <Icon name={icon} size={13} />
      {label}{temperature != null ? ` · ${temperature}°C` : ''}
    </span>
  );
};

// ─── Actions Dropdown ─────────────────────────────────────────────────────────

const ActionsDropdown = ({ log, userCanEdit, onView, onEdit, onDelete, t }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  const items = [
    {
      label: t('dailyLog.actions.view'),
      icon: 'Eye',
      onClick: () => onView(log),
    },
    ...(userCanEdit
      ? [
          {
            label: t('dailyLog.actions.edit'),
            icon: 'Edit2',
            onClick: () => onEdit(log),
          },
          {
            label: t('dailyLog.actions.delete'),
            icon: 'Trash2',
            onClick: () => onDelete(log),
            variant: 'danger',
          },
        ]
      : []),
  ];

  return (
    <div className="relative" onClick={(e) => e?.stopPropagation()}>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 rounded hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
        title={t('dailyLog.table.actions')}
      >
        <Icon name="MoreVertical" size={16} />
      </button>
      <PortalDropdown
        anchorRef={btnRef}
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        width={160}
      />
    </div>
  );
};

// ─── Sort Header ──────────────────────────────────────────────────────────────

// ─── View Modal (PDF-style) ───────────────────────────────────────────────────

const ViewModal = ({ log, project, userCanEdit, onClose, onEdit, onExportPDF, pdfExporting, t }) => {
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const crew = log?.crew || [];
  const photos = log?.daily_log_photos || [];
  const tradesCount = crew?.length;
  const workersSum = crew?.reduce((s, c) => s + (c?.worker_count || 0), 0);
  const creatorName = log?.creator?.full_name || log?.creator?.email || '—';
  const isEdited = log?.updated_at && log?.created_at && log?.updated_at !== log?.created_at;
  const editorName = log?.updater?.full_name || log?.updater?.email || creatorName;

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8 overflow-hidden">

        {/* ── Header band (orange, RFI-style) ── */}
        <div className="bg-orange-600 px-7 py-5 flex items-start justify-between">
          <div className="flex-1">
            <p className="text-orange-200 text-xs font-semibold tracking-widest uppercase mb-1">
              {t('dailyLog.view.title')}
            </p>
            <p className="text-white font-bold text-base leading-tight">
              {project?.projectNumber ? `${project?.projectNumber} — ` : ''}{project?.name || ''}
            </p>
            <p className="text-orange-100 text-sm mt-1">
              {formatDate(log?.log_date)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* ── Accent line ── */}
        <div className="h-0.5 bg-orange-700" />

        {/* ── Title bar ── */}
        <div className="bg-gray-50 border-b border-gray-200 px-7 py-3 text-center">
          <span className="text-xs font-bold text-gray-700 tracking-[0.2em] uppercase">
            {t('dailyLog.view.title')?.toUpperCase()}
          </span>
        </div>

        {/* ── Body ── */}
        <div className="px-7 py-6 space-y-6 max-h-[65vh] overflow-y-auto">

          {/* Section 1: Weather */}
          {(log?.weather_condition || log?.weather_temperature != null) && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-4 bg-primary rounded-full" />
                <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                  {t('dailyLog.view.weather')}
                </span>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
                <WeatherPill condition={log?.weather_condition} temperature={log?.weather_temperature} t={t} />
              </div>
            </div>
          )}

          {/* Section 2: Équipe sur site */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-4 bg-primary rounded-full" />
              <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                {t('dailyLog.crew.sectionTitle')}
              </span>
            </div>
            {crew?.length > 0 ? (
              <div className="space-y-3">
                {crew?.map((c) => (
                  <CrewViewCard key={c?.id} row={c} t={t} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic py-2">{t('dailyLog.crew.emptyState')}</p>
            )}
          </div>

          {/* Section 3: Work Performed */}
          {log?.work_performed?.trim() && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-4 bg-primary rounded-full" />
                <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                  {t('dailyLog.view.workPerformed')}
                </span>
              </div>
              <div className="bg-orange-50 border border-orange-100 rounded-lg px-4 py-3">
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {log?.work_performed}
                </p>
              </div>
            </div>
          )}

          {/* Section 4: Notes (only if filled) */}
          {log?.notes?.trim() && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-4 bg-primary rounded-full" />
                <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                  {t('dailyLog.view.notes')}
                </span>
              </div>
              <div className="border border-gray-200 rounded-lg px-4 py-3">
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {log?.notes}
                </p>
              </div>
            </div>
          )}

          {/* Section 5: Photos (only if any) */}
          {photos?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-4 bg-primary rounded-full" />
                <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                  {t('dailyLog.view.photos')?.replace('{count}', photos?.length)}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {photos?.map((photo) => (
                  <div key={photo?.id} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                    <div
                      className="w-full h-28 bg-gray-100 flex items-center justify-center overflow-hidden cursor-pointer"
                      onClick={() => setLightboxPhoto(photo)}
                    >
                      {photo?.signedUrl ? (
                        <img src={photo?.signedUrl} alt={photo?.caption || 'Photo'} className="w-full h-full object-cover hover:opacity-90 transition-opacity" />
                      ) : (
                        <Icon name="Image" size={28} className="text-gray-300" />
                      )}
                    </div>
                    {photo?.caption && (
                      <p className="px-2 py-1.5 text-xs text-gray-500 truncate">{photo?.caption}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-gray-200 pt-4 space-y-1">
            <p className="text-xs text-gray-400">
              {t('dailyLog.view.createdBy')?.replace('{author}', creatorName)?.replace('{date}', formatDateTime(log?.created_at))}
            </p>
            {isEdited && (
              <p className="text-xs text-gray-400">
                {t('dailyLog.view.editedBy')?.replace('{editor}', editorName)?.replace('{date}', formatDateTime(log?.updated_at))}
              </p>
            )}
            {log?.approval_status === 'approved' && log?.approver ? (
              <p className="text-xs text-green-600 font-medium">
                {t('dailyLog.view.approvedBy')
                  ?.replace('{approver}', log?.approver?.full_name || log?.approver?.email || '—')
                  ?.replace('{date}', formatDateTime(log?.approved_at))}
              </p>
            ) : (
              <p className="text-xs text-gray-400 italic">
                {t('dailyLog.view.awaitingApproval')}
              </p>
            )}
          </div>
        </div>

        {/* ── Action footer ── */}
        <div className="flex items-center justify-between px-7 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('dailyLog.view.close')}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onExportPDF(log)}
              disabled={pdfExporting}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60"
            >
              {pdfExporting ? (
                <Icon name="Loader2" size={15} className="animate-spin" />
              ) : (
                <Icon name="Download" size={15} />
              )}
              {pdfExporting ? (t('dailyLog.view.exportPdf') + '...') : t('dailyLog.view.exportPdf')}
            </button>
            {userCanEdit && (
              <button
                onClick={() => onEdit(log)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors"
              >
                <Icon name="Edit2" size={15} />
                {t('dailyLog.view.edit')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80"
          onClick={() => setLightboxPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] p-4" onClick={(e) => e?.stopPropagation()}>
            <img
              src={lightboxPhoto?.signedUrl}
              alt={lightboxPhoto?.caption || 'Photo'}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
            {lightboxPhoto?.caption && (
              <p className="text-center text-white text-sm mt-2">{lightboxPhoto?.caption}</p>
            )}
            <button
              onClick={() => setLightboxPhoto(null)}
              className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70"
            >
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

// ─── Bulk Action Bar ──────────────────────────────────────────────────────────

// ─── Daily Log Table ──────────────────────────────────────────────────────────

const DailyLogTable = ({
  logs, sortConfig, onSort, onRowClick, onView, onEdit, onDelete,
  userCanEdit, userCanApprove, onApprove, onUnapprove, t,
  selectedIds, onToggleSelect, onToggleSelectAll, lang,
}) => {
  const allSelected = logs?.length > 0 && logs?.every((l) => selectedIds?.has(l?.id));
  const someSelected = logs?.some((l) => selectedIds?.has(l?.id));

  // En-tete triable LOCAL (patron ExtrasTable, mais garde le sortConfig
  // {key, direction} et onSort(sortKey) propres a DailyLog). L'icone reste
  // visible a l'actif et estompee au survol, comme avant.
  const SortableTh = ({ label, sortKey }) => {
    const isActive = sortConfig?.key === sortKey;
    return (
      <th
        className="text-left text-xs font-medium text-gray-500 px-2.5 py-2 cursor-pointer select-none hover:bg-gray-100 transition-colors"
        style={{ whiteSpace: 'nowrap' }}
        onClick={() => onSort(sortKey)}
      >
        <div className="flex items-center gap-1 group">
          <span>{label}</span>
          <span className={`transition-opacity ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}>
            <Icon name={isActive && sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={12} />
          </span>
        </div>
      </th>
    );
  };

  if (logs?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center bg-white border border-gray-200 rounded-xl">
        <Icon name="ClipboardList" size={32} className="text-gray-300 mb-3" />
        <p className="text-gray-500 text-sm">{t('dailyLog.table.noLogs')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-h-0 flex flex-col">
      <div className="min-h-0 overflow-y-auto overflow-x-auto overscroll-contain">
        <table className="w-full">
          <colgroup>
            <col style={{ width: '36px' }} />
            <col style={{ width: '90px' }} />
            <col style={{ width: '130px' }} />
            <col style={{ width: '220px' }} />
            <col style={{ minWidth: '160px' }} />
            <col style={{ width: '70px' }} />
            <col style={{ width: '150px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '48px' }} />
          </colgroup>
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr className="border-b border-gray-200">
              {/* Checkbox header */}
              <th className="px-2 py-2 text-center" style={{ width: '36px' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={onToggleSelectAll}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer accent-primary"
                />
              </th>
              <SortableTh label={t('dailyLog.table.date')} sortKey="date" />
              <SortableTh label={t('dailyLog.table.weather')} sortKey="weather" />
              <SortableTh label={t('dailyLog.table.team')} sortKey="team" />
              <th className="text-left text-xs font-medium text-gray-500 px-2.5 py-2">{t('dailyLog.table.issues')}</th>
              <SortableTh label={t('dailyLog.table.photos')} sortKey="photos" />
              <SortableTh label={t('dailyLog.table.author')} sortKey="author" />
              <th className="text-left text-xs font-medium text-gray-500 px-2.5 py-2" style={{ whiteSpace: 'nowrap' }}>{t('dailyLog.table.approval')}</th>
              <th className="text-left text-xs font-medium text-gray-500 px-2.5 py-2" style={{ whiteSpace: 'nowrap' }}>{t('dailyLog.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {logs?.map((log) => {
              const crew = log?.crew || [];
              const photos = log?.daily_log_photos || [];
              const photosCount = photos?.length;
              const hasNotes = !!(log?.notes?.trim());
              const creatorName = log?.creator?.full_name || log?.creator?.email || '—';
              const isEdited = log?.updated_at && log?.created_at && log?.updated_at !== log?.created_at;
              const isSelected = selectedIds?.has(log?.id);

              // ── Resumes repris VERBATIM de previewCard2-5 de la modale ──
              // Meteo
              const weatherCond = log?.weather_condition;
              const weatherTemp = log?.weather_temperature;
              const weatherSummary = weatherCond && weatherTemp != null
                ? `${WEATHER_ICONS?.[weatherCond] || ''} ${weatherCond} · ${weatherTemp}°C`
                : weatherCond
                  ? `${WEATHER_ICONS?.[weatherCond] || ''} ${weatherCond}`
                  : null;
              // Equipe
              const subcount = crew?.length || 0;
              const workercount = crew?.reduce((sum, c) => sum + (c?.worker_count || 0), 0) || 0;
              const taskcount = crew?.reduce((sum, c) => sum + (c?.tasks?.length || 0), 0) || 0;
              const teamSummary = subcount === 0
                ? t('dailyLog.preview.noSubcontractor')
                : `${subcount} ${t('dailyLog.preview.subcontractors')} · ${workercount} ${t('dailyLog.preview.workers')} · ${taskcount} ${t('dailyLog.preview.tasks')}`;
              // Enjeux
              const issueVal = log?.issue_type || '';
              const issueDescVal = log?.issue_description || '';
              const issueLabel = issueVal
                ? (ISSUE_TYPE_LABELS?.[issueVal]?.[lang === 'fr' ? 'fr' : 'en'] || issueVal)
                : '';
              const issuesSummary = issueVal
                ? `${issueLabel}${issueDescVal ? ' · ' + issueDescVal?.slice(0, 40) + (issueDescVal?.length > 40 ? '…' : '') : ''}`
                : null;

              return (
                <tr
                  key={log?.id}
                  className={`cursor-pointer transition-colors ${isSelected ? 'bg-orange-50 hover:bg-orange-100/70' : 'hover:bg-gray-50/60'}`}
                  onClick={() => onRowClick(log)}
                >
                  {/* Checkbox */}
                  <td className="px-2 py-2 text-center align-middle" onClick={(e) => e?.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelect(log?.id)}
                      className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer accent-primary"
                    />
                  </td>
                  {/* Date */}
                  <td className="px-2.5 py-2 align-middle">
                    <span className="text-xs font-medium text-gray-900 leading-tight whitespace-nowrap">
                      {formatDateShort(log?.log_date, lang)}
                    </span>
                  </td>
                  {/* Conditions (meteo) */}
                  <td className="px-2.5 py-2 align-middle">
                    {weatherSummary
                      ? <span className="block text-xs text-gray-600 truncate" title={weatherSummary}>{weatherSummary}</span>
                      : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  {/* Equipe */}
                  <td className="px-2.5 py-2 align-middle">
                    <span className="block text-xs text-gray-600 truncate" title={teamSummary}>{teamSummary}</span>
                  </td>
                  {/* Enjeux */}
                  <td className="px-2.5 py-2 align-middle">
                    {issuesSummary
                      ? <span className="block text-xs text-gray-600 truncate" title={issuesSummary}>{issuesSummary}</span>
                      : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  {/* Photos */}
                  <td className="px-2.5 py-2 align-middle">
                    {photosCount > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                        <Icon name="Camera" size={11} className="text-gray-400" />
                        {photosCount}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  {/* Author */}
                  <td className="px-2.5 py-2 align-middle">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div
                        className="w-5 h-5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0"
                        title={creatorName}
                      >
                        {creatorName?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-900 leading-tight truncate">{creatorName}</p>
                        {isEdited && (
                          <p className="text-[10px] text-gray-400 leading-tight">{t('dailyLog.table.edited')}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  {/* Approval */}
                  <td className="px-2.5 py-2 align-middle" onClick={(e) => e?.stopPropagation()}>
                    <ApprovalPill
                      log={log}
                      userCanApprove={userCanApprove}
                      onApprove={onApprove}
                      onUnapprove={onUnapprove}
                      t={t}
                    />
                  </td>
                  {/* Actions */}
                  <td className="px-2.5 py-2 align-middle" onClick={(e) => e?.stopPropagation()}>
                    <ActionsDropdown
                      log={log}
                      userCanEdit={userCanEdit}
                      onView={onView}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      t={t}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Date Range Filter ────────────────────────────────────────────────────────

const DATE_RANGE_OPTIONS = [
  { value: '7', labelFr: '7 derniers jours', labelEn: 'Last 7 days' },
  { value: '15', labelFr: '15 derniers jours', labelEn: 'Last 15 days' },
  { value: '30', labelFr: '30 derniers jours', labelEn: 'Last 30 days' },
  { value: '90', labelFr: '90 derniers jours', labelEn: 'Last 90 days' },
  { value: 'year', labelFr: 'Cette année', labelEn: 'This year' },
  { value: 'custom', labelFr: 'Personnalisé', labelEn: 'Custom' },
];

const computeDateRange = (value) => {
  const today = new Date();
  today?.setHours(0, 0, 0, 0);
  const toStr = (d) => d?.toISOString()?.split('T')?.[0];
  if (value === 'year') {
    return { dateFrom: `${today?.getFullYear()}-01-01`, dateTo: toStr(today) };
  }
  if (value === 'custom' || !value) return { dateFrom: '', dateTo: '' };
  const days = parseInt(value, 10);
  const from = new Date(today);
  from.setDate(today?.getDate() - days + 1);
  return { dateFrom: toStr(from), dateTo: toStr(today) };
};

const DateRangeFilter = ({ dateRangeOption, onDateRangeChange, customFrom, customTo, onCustomFromChange, onCustomToChange, filteredCount, lang, t }) => {
  const label = (opt) => lang === 'fr' ? opt?.labelFr : opt?.labelEn;
  const selectedOpt = DATE_RANGE_OPTIONS?.find((o) => o?.value === dateRangeOption);
  const selectedLabel = selectedOpt ? label(selectedOpt) : (lang === 'fr' ? '30 derniers jours' : 'Last 30 days');

  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Compact custom dropdown — no native <select>, no double arrow */}
      <div className="relative">
        <button
          ref={btnRef}
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="h-7 pl-7 pr-7 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white cursor-pointer text-gray-600 flex items-center gap-1.5 hover:border-gray-300 transition-colors"
        >
          {selectedLabel}
        </button>
        {/* Calendar icon — left */}
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
          <Icon name="Calendar" size={12} />
        </span>
        {/* Single chevron icon — right */}
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
          <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={11} />
        </span>
        <PortalDropdown anchorRef={btnRef} open={open} onClose={() => setOpen(false)} width={200} align="left">
          {DATE_RANGE_OPTIONS?.map((opt) => {
            const isSelected = opt?.value === dateRangeOption;
            return (
              <button
                key={opt?.value}
                type="button"
                onMouseDown={() => {
                  onDateRangeChange(opt?.value);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 transition-colors rounded-md mx-0 ${
                  isSelected
                    ? 'bg-orange-50 text-primary' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>{label(opt)}</span>
                {isSelected && <Icon name="Check" size={13} className="text-primary shrink-0" />}
              </button>
            );
          })}
        </PortalDropdown>
      </div>
      {/* Custom date inputs — only shown when "custom" is selected */}
      {dateRangeOption === 'custom' && (
        <>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500 whitespace-nowrap">{t('dailyLog.filters.dateFrom')}</label>
            <input
              type="date"
              value={customFrom}
              onChange={(e) => onCustomFromChange(e?.target?.value)}
              className="h-9 text-sm border border-gray-300 rounded-lg px-2.5 focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-500 whitespace-nowrap">{t('dailyLog.filters.dateTo')}</label>
            <input
              type="date"
              value={customTo}
              onChange={(e) => onCustomToChange(e?.target?.value)}
              className="h-9 text-sm border border-gray-300 rounded-lg px-2.5 focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </>
      )}
      {/* Log counter */}
      <span className="text-xs text-gray-500 font-medium">
        {filteredCount} {lang === 'fr' ? (filteredCount !== 1 ? 'journaux' : 'journal') : 'log' + (filteredCount !== 1 ? 's' : '')}
      </span>
    </div>
  );
};

// ─── Approval Pill ────────────────────────────────────────────────────────────

const ApprovalPill = ({ log, userCanApprove, onApprove, onUnapprove, t }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const btnRef = useRef(null);
  const status = log?.approval_status || 'submitted';

  const pillClass = status === 'approved' ?'bg-green-100 text-green-700 border border-green-200' :'bg-yellow-100 text-yellow-700 border border-yellow-200';

  const label = status === 'approved' ? t('dailyLog.approval.approved')
    : t('dailyLog.approval.submitted');

  if (!userCanApprove) {
    return (
      <Badge variant={statusVariant('dailyLog', status)}>
        {label}
      </Badge>
    );
  }

  return (
    <div className="relative" onClick={(e) => e?.stopPropagation()}>
      <button
        ref={btnRef}
        onClick={() => !loading && setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${pillClass}`}
      >
        {label}
        <Icon name={loading ? 'Loader2' : 'ChevronDown'} size={11} className={loading ? 'animate-spin' : ''} />
      </button>
      {/* Pas de prop `items` : PortalDropdown rend `children` UNIQUEMENT quand
          items est absent — un tableau vide est truthy et donnait un menu vide. */}
      <PortalDropdown anchorRef={btnRef} open={open} onClose={() => !loading && setOpen(false)}>
        {status === 'submitted' ? (
          <button
            disabled={loading}
            className="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50 flex items-center gap-2 disabled:opacity-50"
            onClick={async () => {
              setLoading(true);
              try {
                await onApprove(log);
              } finally {
                setLoading(false);
                setOpen(false);
              }
            }}
          >
            <Icon name="CheckCircle2" size={14} />
            {t('dailyLog.approval.markApproved')}
          </button>
        ) : (
          <button
            disabled={loading}
            className="w-full text-left px-4 py-2 text-sm text-yellow-700 hover:bg-yellow-50 flex items-center gap-2 disabled:opacity-50"
            onClick={async () => {
              setLoading(true);
              try {
                await onUnapprove(log);
              } finally {
                setLoading(false);
                setOpen(false);
              }
            }}
          >
            <Icon name="RotateCcw" size={14} />
            {t('dailyLog.approval.markSubmitted')}
          </button>
        )}
      </PortalDropdown>
    </div>
  );
};

// ─── Crew View Card (read-only, expandable) ───────────────────────────────────

const CrewViewCard = ({ row, t }) => {
  const [expanded, setExpanded] = useState(true);
  const subName = row?.subcontractor?.company_name || row?.subcontractors?.company_name || row?.subcontractor_name || '—';
  const trade = row?.subcontractor?.trade || row?.subcontractors?.trade || null;
  const tasks = (row?.tasks || [])?.slice()?.sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0));

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Card header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium text-gray-900 text-sm truncate">{subName}</span>
          {trade && (
            <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
              {trade}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          <span className="text-xs text-gray-500">👤 {row?.worker_count || 0} {t('dailyLog.crew.cardHeader.workers')}</span>
          <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-gray-400" />
        </div>
      </button>
      {/* Card body */}
      {expanded && (
        <div className="px-3 pb-3 border-t border-gray-100 pt-2 space-y-2">
          {row?.notes && (
            <p className="text-xs text-gray-500 italic">{row?.notes}</p>
          )}
          {tasks?.length > 0 ? (
            <div className="space-y-1.5">
              {tasks?.map((task) => (
                <div key={task?.id} className="flex items-center gap-2">
                  <span className="text-xs text-gray-700 w-32 flex-shrink-0 truncate">{task?.nom || '—'}</span>
                  <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${Math.min(100, Math.max(0, task?.pourcentage || 0))}%`, backgroundColor: '#ed7340' }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-8 text-right flex-shrink-0">{task?.pourcentage ?? 0}%</span>
                  {task?.completed && (
                    <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                      <Icon name="Check" size={10} />
                      {t('dailyLog.taskCompleted')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">{t('dailyLog.crew.noTasks')}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Crew Edit Card (editable, always expanded) ───────────────────────────────

const CrewEditCard = ({ row, index, subcontractors, projectIntervenants, onChange, onRemove, onAddToRepertoire, t, lang, hasValidationError }) => {
  const addTaskRow = () => {
    const newTasks = [...(row?.tasks || []), { tempId: `t_${Date.now()}_${Math.random()}`, nom: '', pourcentage: 0 }];
    onChange(index, 'tasks', newTasks);
  };

  const removeTaskRow = (taskIdx) => {
    const newTasks = (row?.tasks || [])?.filter((_, i) => i !== taskIdx);
    onChange(index, 'tasks', newTasks);
  };

  const updateTaskField = (taskIdx, field, value) => {
    const newTasks = (row?.tasks || [])?.map((t, i) => i === taskIdx ? { ...t, [field]: value } : t);
    onChange(index, 'tasks', newTasks);
  };

  const updateTaskFields = (taskIdx, patches) => {
    const newTasks = (row?.tasks || [])?.map((t, i) =>
      i === taskIdx ? { ...t, ...patches } : t
    );
    onChange(index, 'tasks', newTasks);
  };

  const handlePercentageBlur = (taskIdx, value) => {
    const task = row?.tasks?.[taskIdx];
    const minPct = task?._minPercent ?? 0;
    const parsed = parseInt(value) || 0;
    if (parsed < minPct) {
      updateTaskFields(taskIdx, { pourcentage: minPct, _percentError: t('dailyLog.cannotReduce')?.replace('{n}', minPct) });
    } else {
      updateTaskFields(taskIdx, { _percentError: null });
    }
  };

  // Carry-forward lock: sub is locked if any task has previous_task_id
  const isCarryForwardSub = row?.tasks?.some(t => t?.previous_task_id != null);

  // Use project_intervenants if available, fall back to subcontractors
  const pickerList = projectIntervenants?.length > 0 ? projectIntervenants : null;

  return (
    <div className={`bg-white rounded-lg border overflow-hidden ${hasValidationError ? 'border-red-400 border-2' : 'border-gray-200'}`}>
      {/* Card header */}
      <div className="flex items-start gap-2 px-3 py-2.5 bg-gray-50 border-b border-gray-200">
        {/* Subcontractor dropdown + worker count in flex row, items-end */}
        <div className="flex-1 flex flex-col md:flex-row md:items-end gap-2">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('dailyLog.crew.subcontractorLabel')}</label>
            {pickerList === null ? (
              /* Empty state: no project intervenants */
              (<div className="w-full text-sm border border-gray-200 rounded-md px-2.5 py-2 bg-gray-50 text-gray-400 italic">
                {t('dailyLog.picker.empty')}
              </div>)
            ) : (
              <select
                value={row?.subcontractorId || ''}
                onChange={(e) => onChange(index, 'subcontractorId', e?.target?.value)}
                className="w-full h-10 text-sm border border-gray-300 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
              >
                <option value="">{t('dailyLog.crew.subcontractorPlaceholder')}</option>
                {pickerList?.map((s) => (
                  <option key={s?.id} value={s?.id}>
                    {s?.company_name}{s?.trade ? ` — ${s?.trade}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="w-full md:w-28 flex-shrink-0">
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('dailyLog.crew.workerCountLabel')}</label>
            <input
              type="number"
              min="0"
              value={row?.workerCount ?? ''}
              onChange={(e) => onChange(index, 'workerCount', e?.target?.value !== '' ? parseInt(e?.target?.value) : '')}
              className="w-full h-10 text-sm border border-gray-300 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="0"
            />
          </div>
        </div>
        {isCarryForwardSub ? (
          <button
            type="button"
            disabled
            className="mt-5 p-1.5 text-red-400 opacity-40 cursor-not-allowed rounded flex-shrink-0"
            title={t('dailyLog.locked.subcontractor')}
          >
            🗑
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="mt-5 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
            title={t('dailyLog.crew.remove')}
          >
            🗑
          </button>
        )}
      </div>
      {/* Card body */}
      <div className="px-3 py-2.5 space-y-3">
        {/* Tasks — moved above Notes */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5">{t('dailyLog.crew.label')}</label>
          {(row?.tasks || [])?.length === 0 ? (
            <p className="text-xs text-gray-400 italic mb-1.5">{t('dailyLog.crew.noTasks')}</p>
          ) : (
            <div className="space-y-1.5 mb-1.5">
              {(row?.tasks || [])?.map((task, taskIdx) => {
                const minPct = task?._minPercent ?? 0;
                // Task is locked if carried forward AND pourcentage > 0
                const isTaskLocked = task?.previous_task_id != null && (task?.pourcentage || 0) > 0;
                return (
                  <div key={task?.id || task?.tempId} className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={task?.nom || ''}
                        onChange={(e) => updateTaskField(taskIdx, 'nom', e?.target?.value)}
                        placeholder={t('dailyLog.crew.taskName')}
                        className="flex-1 text-sm border border-gray-300 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/20"
                      />
                      <div className="flex flex-col items-end gap-0.5">
                        <input
                          type="number"
                          max={100}
                          step={1}
                          inputMode="numeric"
                          value={task?.pourcentage ?? ''}
                          onFocus={(e) => {
                            const t = e?.target;
                            requestAnimationFrame(() => t?.select());
                          }}
                          onChange={(e) => {
                            const raw = e?.target?.value;
                            if (raw === '') {
                              updateTaskFields(taskIdx, { pourcentage: '', _percentError: null });
                              return;
                            }
                            const num = parseInt(raw, 10);
                            if (Number.isNaN(num)) return;
                            const clamped = Math.max(0, Math.min(100, num));
                            updateTaskFields(taskIdx, { pourcentage: clamped, _percentError: null });
                          }}
                          onBlur={(e) => {
                            const num = parseInt(e?.target?.value, 10);
                            const minPct = task?._minPercent;
                            if (Number.isFinite(num) && minPct !== undefined && num < minPct) {
                              updateTaskFields(taskIdx, {
                                pourcentage: minPct,
                                _percentError: `Impossible de réduire l'avancement. Minimum: ${minPct}%`
                              });
                              return;
                            }
                            if (e?.target?.value === '') {
                              updateTaskFields(taskIdx, {
                                pourcentage: minPct ?? 0,
                                _percentError: null
                              });
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e?.key === 'Enter') e?.preventDefault();
                          }}
                          placeholder="%"
                          className={`w-16 text-sm border rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/20 text-right ${task?._percentError ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        {minPct > 0 && (
                          <span className="text-xs text-gray-400">{t('dailyLog.previousPercent')?.replace('{n}', minPct)}</span>
                        )}
                      </div>
                      {isTaskLocked ? (
                        <button
                          type="button"
                          disabled
                          className="p-1 text-red-400 opacity-40 cursor-not-allowed rounded flex-shrink-0"
                          title={t('dailyLog.locked.task')}
                        >
                          🗑
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => removeTaskRow(taskIdx)}
                          className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                        >
                          🗑
                        </button>
                      )}
                    </div>
                    {task?._percentError && (
                      <p className="text-xs text-red-600 ml-0">{task?._percentError}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {/* ONE add-task button per subcontractor card */}
          <button
            type="button"
            data-add-task-btn
            onClick={addTaskRow}
            className="text-xs font-medium text-orange-600 hover:text-orange-700 flex items-center gap-1"
          >
            <Icon name="Plus" size={12} />
            {t('dailyLog.crew.addTask')}
          </button>
        </div>

        {/* Notes — moved below Tasks */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('dailyLog.crew.notesLabel')}</label>
          <textarea
            value={row?.notes || ''}
            onChange={(e) => onChange(index, 'notes', e?.target?.value)}
            rows={2}
            className="w-full text-sm border border-gray-300 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
            placeholder={t('dailyLog.crew.notes')}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Photo Item ────────────────────────────────────────────────────────────────

const PhotoItem = ({ photo, onRemove, onCaptionChange, t, isExisting }) => (
  <div className="relative group border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
    <div className="w-full h-28 bg-gray-100 flex items-center justify-center overflow-hidden">
      {(photo?.signedUrl || photo?.previewUrl) ? (
        <img src={photo?.signedUrl || photo?.previewUrl} alt={photo?.caption || 'Photo'} className="w-full h-full object-cover" />
      ) : (
        <Icon name="Image" size={28} className="text-gray-300" />
      )}
    </div>
    <div className="p-2">
      <input
        type="text"
        value={photo?.caption || ''}
        onChange={(e) => onCaptionChange(photo?.id || photo?.tempId, e?.target?.value, isExisting)}
        placeholder={t('dailyLog.photos.caption')}
        className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
    </div>
    <button
      type="button"
      onClick={() => onRemove(photo?.id || photo?.tempId, isExisting)}
      className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
      title={t('dailyLog.photos.remove')}
    >
      <Icon name="X" size={12} />
    </button>
  </div>
);

// ─── Daily Log Modal ───────────────────────────────────────────────────────────

const DailyLogModal = ({ log, projectId, organizationId, userId, subcontractors, existingDates, logs, onClose, onSaved, t, lang, initialCrew }) => {
  const isEdit = !!log;
  // Un journal approuve garde sa date : l'approbation porte sur un jour precis.
  const dateLocked = isEdit && log?.approval_status === 'approved';
  const dropzoneRef = useRef(null);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    logDate: log?.log_date || todayStr(),
    weatherCondition: log?.weather_condition || '',
    weatherTemperature: log?.weather_temperature ?? '',
    workPerformed: log?.work_performed || '',
    notes: log?.notes || '',
    materialsDelivered: log?.materials_delivered || '',
    issueType: log?.issue_type || '',
    issueDescription: log?.issue_description || '',
  });

  // Project intervenants (scoped to current project)
  const [projectIntervenants, setProjectIntervenants] = useState(null); // null = loading, [] = empty

  useEffect(() => {
    const fetchIntervenants = async () => {
      try {
        const { data } = await supabase
          ?.from('project_intervenants')
          ?.select('subcontractor_id, subcontractors(id, company_name, trade)')
          ?.eq('project_id', projectId);
        // Flatten to { id, company_name, trade }
        const list = (data || [])
          ?.map((row) => row?.subcontractors)
          ?.filter(Boolean)
          ?.sort((a, b) => (a?.company_name || '')?.localeCompare(b?.company_name || ''));
        setProjectIntervenants(list);
      } catch (err) {
        console.warn('Failed to fetch project intervenants:', err);
        setProjectIntervenants([]);
      }
    };
    if (projectId) fetchIntervenants();
  }, [projectId]);

  // Initialize crew from log (edit mode), initialCrew (auto-populate), or empty
  const [crew, setCrew] = useState(() => {
    if (log?.crew) {
      return (log?.crew || [])?.map((c) => ({
        id: c?.id || null,
        subcontractorId: c?.subcontractor?.id || c?.subcontractors?.id || c?.subcontractor_id || '',
        workerCount: c?.worker_count ?? '',
        notes: c?.notes || '',
        tasks: (c?.tasks || [])?.slice()?.sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0))?.map((t) => ({
          id: t?.id || null,
          nom: t?.nom || '',
          pourcentage: t?.pourcentage ?? 0,
          sort_order: t?.sort_order ?? 0,
          previous_task_id: t?.previous_task_id || null,
          completed: t?.completed || false,
          completed_at: t?.completed_at || null,
        })),
      }));
    }
    if (initialCrew) {
      return initialCrew;
    }
    return [];
  });

  const [existingPhotos, setExistingPhotos] = useState(log?.daily_log_photos || []);
  // Les legendes telles qu'elles ont ete chargees. Figees au premier rendu,
  // comme existingPhotos : c'est la seule reference fiable pour savoir ce que
  // l'utilisateur a reellement change, et elle ne bouge pas si le parent
  // rafraichit `log` pendant que la modale est ouverte.
  const originalCaptions = useRef(
    Object.fromEntries((log?.daily_log_photos || [])?.map((p) => [p?.id, p?.caption ?? '']))
  );
  const [newPhotos, setNewPhotos] = useState([]);
  const [deletedPhotoIds, setDeletedPhotoIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [error, setError] = useState(null);
  const [dateError, setDateError] = useState(null);
  // Equipe reportee du journal precedent (creation). Elle suit la date choisie.
  const [carriedCrew, setCarriedCrew] = useState(initialCrew || null);
  // L'equipe telle qu'elle a ete pre-remplie la derniere fois : tant que
  // `crew` lui est identique, l'utilisateur n'y a pas touche.
  const prefillSnapshot = useRef(JSON.stringify(initialCrew || []));
  // Changement de date en attente d'une confirmation : { crew, previousDate }.
  const [pendingPrefill, setPendingPrefill] = useState(null);
  const prefillRequest = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [crewValidationErrors, setCrewValidationErrors] = useState([]);
  const [validationModal, setValidationModal] = useState({ open: false, messages: [], invalidBlocks: [] });

  const totalPhotos = existingPhotos?.length + newPhotos?.length;

  // Date du journal d'ou viennent les taches reportees de CE journal (la plus
  // recente s'il y en a plusieurs), lue dans la liste deja chargee. Null si
  // aucune tache n'est reportee, ou si le journal source n'est pas dans la liste.
  const carriedSourceDate = (crewRows) => {
    const sourceIds = new Set(
      (crewRows || [])?.flatMap((row) => (row?.tasks || [])?.map((task) => task?.previous_task_id)?.filter(Boolean))
    );
    if (sourceIds?.size === 0) return null;
    let latest = null;
    (logs || [])?.forEach((other) => {
      const holds = (other?.crew || [])?.some((c) => (c?.tasks || [])?.some((task) => sourceIds?.has(task?.id)));
      if (holds && (!latest || other?.log_date > latest)) latest = other?.log_date;
    });
    return latest;
  };

  // Date du premier journal qui REPREND des taches de celui-ci (modification
  // seulement), lue dans la liste deja chargee. Null si aucun n'en reprend.
  const dependentDate = () => {
    const myTaskIds = new Set((log?.crew || [])?.flatMap((c) => (c?.tasks || [])?.map((task) => task?.id)?.filter(Boolean)));
    if (myTaskIds?.size === 0) return null;
    let earliest = null;
    (logs || [])?.forEach((other) => {
      if (other?.id === log?.id) return;
      const reprises = (other?.crew || [])?.some((c) => (c?.tasks || [])?.some((task) => task?.previous_task_id && myTaskIds?.has(task?.previous_task_id)));
      if (reprises && (!earliest || other?.log_date < earliest)) earliest = other?.log_date;
    });
    return earliest;
  };

  // Probleme de la date, ou null. Creation ET modification : jamais dans le
  // futur, jamais un jour qui a deja un journal (sauf ce journal lui-meme),
  // jamais avant le journal d'ou viennent ses taches reportees, ni (en
  // modification) apres un journal qui reprend les siennes.
  const dateProblem = (value, crewRows) => {
    // En modification, une date inchangee n'est jamais remise en cause : un
    // ancien journal garde la sienne, et ses autres champs restent modifiables.
    if (isEdit && value === log?.log_date) return null;
    if (!value) return t('dailyLog.modal.datePlaceholder');
    if (value > todayStr()) return t('dailyLog.modal.dateFuture');
    const taken = (logs || [])?.some((other) => other?.id !== log?.id && other?.log_date === value);
    if (taken || (!logs && !isEdit && existingDates?.includes(value))) return t('dailyLog.modal.dateAlreadyExists');
    const sourceDate = carriedSourceDate(crewRows);
    if (sourceDate && value <= sourceDate) {
      return t('dailyLog.modal.dateBeforeSource')?.replace('{date}', formatDateShort(sourceDate, lang));
    }
    const dependent = isEdit ? dependentDate() : null;
    if (dependent && value >= dependent) {
      return t('dailyLog.modal.dateAfterDependent')?.replace('{date}', formatDateShort(dependent, lang));
    }
    return null;
  };

  // Creation seulement : l'equipe pre-remplie vient du dernier journal
  // STRICTEMENT avant la date choisie. Equipe non touchee : remplacement
  // silencieux. Equipe modifiee : on demande avant de remplacer.
  const refreshPrefill = async (value, previousDate) => {
    const request = ++prefillRequest.current;
    let next = [];
    try {
      const { data: prevLog } = await getPreviousLogWithCrew(projectId, value);
      next = carriedCrewFrom(prevLog) || [];
    } catch (err) {
      console.warn('Auto-populate failed for the chosen date:', err);
      return;
    }
    // Une date choisie depuis rend cette reponse perimee.
    if (request !== prefillRequest.current) return;
    const untouched = JSON.stringify(crew) === prefillSnapshot.current;
    if (untouched) {
      applyPrefill(next);
    } else if (JSON.stringify(next) !== prefillSnapshot.current) {
      setPendingPrefill({ crew: next, previousDate });
    }
  };

  const applyPrefill = (next) => {
    setCrew(next);
    setCarriedCrew(next?.length > 0 ? next : null);
    prefillSnapshot.current = JSON.stringify(next);
    setCrewValidationErrors([]);
  };

  const handleFormChange = (field, value) => {
    if (field === 'logDate') {
      const previousDate = form?.logDate;
      setForm((prev) => ({ ...prev, logDate: value }));
      if (isEdit) {
        // En modification, les taches et leurs liens ne bougent pas.
        setDateError(dateProblem(value, crew));
        return;
      }
      // En creation, l'equipe reportee sera recalculee pour cette date : le
      // controle « avant le journal source » se fera sur la nouvelle equipe.
      const problem = dateProblem(value, []);
      setDateError(problem);
      if (!problem && value !== previousDate) refreshPrefill(value, previousDate);
      return;
    }
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const addCrewRow = () => setCrew((prev) => [...prev, { id: null, subcontractorId: '', workerCount: '', notes: '', tasks: [] }]);
  const removeCrewRow = (idx) => setCrew((prev) => prev?.filter((_, i) => i !== idx));
  const updateCrewRow = (idx, field, value) => {
    setCrew((prev) => prev?.map((row, i) => i === idx ? { ...row, [field]: value } : row));
    // Clear validation error for this row when user modifies it
    setCrewValidationErrors((prev) => prev?.filter((e) => e?.idx !== idx));
  };

  const addFiles = useCallback((files) => {
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    const MAX_BYTES = 20 * 1024 * 1024;

    const fileArray = Array.from(files);
    const validItems = [];

    for (const f of fileArray) {
      const type = f?.type?.toLowerCase();
      // HEIC/HEIF: browser may report empty type — check extension too
      const isHeic = type === 'image/heic' || type === 'image/heif'
        || f?.name?.toLowerCase()?.endsWith('.heic')
        || f?.name?.toLowerCase()?.endsWith('.heif');

      if (isHeic) {
        // Check if createImageBitmap can handle it (Safari yes, Chrome no)
        if (typeof createImageBitmap === 'undefined') {
          setError(t('dailyLog.photos.heicNotSupported'));
          continue;
        }
        // We'll attempt it; if it fails during compression the service will skip it
      } else if (!ALLOWED_TYPES?.includes(type)) {
        continue;
      }

      if (f?.size > MAX_BYTES) {
        setError(t('dailyLog.photos.fileTooLarge'));
        continue;
      }

      validItems?.push(f);
    }

    const remaining = 20 - totalPhotos;
    const toAdd = validItems?.slice(0, remaining);
    const newItems = toAdd?.map((f) => ({
      tempId: `new_${Date.now()}_${Math.random()}`,
      file: f,
      previewUrl: URL.createObjectURL(f),
      caption: '',
    }));
    setNewPhotos((prev) => [...prev, ...newItems]);
  }, [totalPhotos, t]);

  const handleFileInput = (e) => {
    addFiles(e?.target?.files || []);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    addFiles(e?.dataTransfer?.files || []);
  };

  const removeExistingPhoto = (photoId) => {
    setExistingPhotos((prev) => prev?.filter((p) => p?.id !== photoId));
    setDeletedPhotoIds((prev) => [...prev, photoId]);
  };

  const removeNewPhoto = (tempId) => {
    setNewPhotos((prev) => {
      const photo = prev?.find((p) => p?.tempId === tempId);
      if (photo?.previewUrl) URL.revokeObjectURL(photo?.previewUrl);
      return prev?.filter((p) => p?.tempId !== tempId);
    });
  };

  const updateExistingCaption = (photoId, caption) => {
    setExistingPhotos((prev) => prev?.map((p) => p?.id === photoId ? { ...p, caption } : p));
  };

  const updateNewCaption = (tempId, caption) => {
    setNewPhotos((prev) => prev?.map((p) => p?.tempId === tempId ? { ...p, caption } : p));
  };

  const handleSave = async () => {
    // La date d'un journal approuve est verrouillee : elle n'est pas recontrolee.
    const problem = dateLocked ? null : dateProblem(form?.logDate, crew);
    if (problem) {
      setDateError(problem);
      return;
    }

    // ── Pre-save crew validation ──
    const invalidBlocks = [];
    crew?.forEach((row, idx) => {
      const tasksList = row?.tasks || [];
      const subName = projectIntervenants?.find((s) => s?.id === row?.subcontractorId)?.company_name || `Sous-traitant #${idx + 1}`;
      if (tasksList?.length === 0) {
        invalidBlocks?.push({ idx, reason: 'no_task', subName });
        return;
      }
      tasksList?.forEach((task, tIdx) => {
        const pct = task?.pourcentage;
        const isValidPct = pct !== '' && pct !== null && pct !== undefined && Number.isFinite(Number(pct));
        if (!isValidPct) {
          const taskName = task?.nom || `Tâche #${tIdx + 1}`;
          invalidBlocks?.push({ idx, tIdx, reason: 'invalid_pct', subName, taskName });
        }
      });
    });

    if (invalidBlocks?.length > 0) {
      setCrewValidationErrors(invalidBlocks);

      const messages = invalidBlocks?.map((b) => {
        if (b?.reason === 'no_task') {
          return t('dailyLog.validation.noTask')?.replace('{subName}', b?.subName);
        }
        return t('dailyLog.validation.invalidPct')?.replace('{subName}', b?.subName)?.replace('{taskName}', b?.taskName);
      });

      // Show in-app modal instead of window.alert
      setValidationModal({ open: true, messages, invalidBlocks });

      // Scroll to first invalid card
      const firstError = invalidBlocks?.[0];
      const cardEl = document.querySelector(`[data-crew-index="${firstError?.idx}"]`);
      if (cardEl) {
        cardEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (newPhotos?.length > 0) {
      setCompressing(true);
      setUploadingCount(newPhotos?.length);
    }
    setSaving(true);
    setError(null);
    try {
      for (const pid of deletedPhotoIds) {
        await deletePhoto(pid);
      }

      if (isEdit) {
        // ── Step 1: Update log fields ──
        const { error: updateErr } = await update(log?.id, {
          weatherCondition: form?.weatherCondition || null,
          weatherTemperature: form?.weatherTemperature !== '' ? parseFloat(form?.weatherTemperature) : null,
          workPerformed: form?.workPerformed,
          notes: form?.notes || null,
          materialsDelivered: form?.materialsDelivered || null,
          issueType: form?.issueType || null,
          issueDescription: form?.issueDescription || null,
          crew: [], // we handle crew manually below
          newPhotos: newPhotos?.map((p) => ({ file: p?.file, caption: p?.caption })),
          userId,
          projectId,
          skipCrewDelete: true,
          // La date n'est envoyee que si elle a change.
          ...(!dateLocked && form?.logDate !== log?.log_date ? { logDate: form?.logDate } : {}),
        });
        if (updateErr) {
          // Un autre journal a pris cette date entre-temps : meme message, jamais l'erreur brute.
          if (updateErr?.code === '23505') {
            setDateError(t('dailyLog.modal.dateAlreadyExists'));
            setSaving(false);
            setCompressing(false);
            setUploadingCount(0);
            return;
          }
          throw updateErr;
        }

        // ── Step 1b: Legendes des photos deja enregistrees ──
        // Elles partent au moment d'Enregistrer, dans le meme bloc que le
        // reste de la modale. On n'envoie que ce qui differe de l'etat
        // charge : rien de change, aucun aller-retour.
        // Une legende videe compte comme un changement — elle repart en null.
        const captionChanges = existingPhotos
          ?.filter(
            (p) =>
              p?.id &&
              (p?.caption ?? '') !== (originalCaptions?.current?.[p?.id] ?? '')
          )
          ?.map((p) => ({ id: p?.id, caption: p?.caption }));

        if (captionChanges?.length > 0) {
          const { error: captionErr } = await updatePhotoCaptions(captionChanges);
          if (captionErr) throw captionErr;
        }

        // ── Step 2: Diff-and-patch crew rows ──
        const originalCrewIds = new Set((log?.crew || [])?.map((c) => c?.id));
        const currentCrewIds = new Set(crew?.filter((c) => c?.id)?.map((c) => c?.id));

        // Delete removed crew rows (cascade removes tasks)
        for (const origRow of (log?.crew || [])) {
          if (!currentCrewIds?.has(origRow?.id)) {
            await supabase?.from('daily_log_crew')?.delete()?.eq('id', origRow?.id);
          }
        }

        for (const crewRow of crew) {
          if (!crewRow?.subcontractorId) continue; // skip empty rows

          if (crewRow?.id) {
            // PATCH existing crew row
            await supabase?.from('daily_log_crew')?.update({
              subcontractor_id: crewRow?.subcontractorId,
              worker_count: crewRow?.workerCount || null,
              notes: crewRow?.notes || null,
            })?.eq('id', crewRow?.id);

            // Diff tasks for this crew row
            const origRow = (log?.crew || [])?.find((r) => r?.id === crewRow?.id);
            const origTaskIds = new Set((origRow?.tasks || [])?.map((t) => t?.id));
            const currentTaskIds = new Set((crewRow?.tasks || [])?.filter((t) => t?.id)?.map((t) => t?.id));

            // Delete removed tasks
            for (const origTask of (origRow?.tasks || [])) {
              if (!currentTaskIds?.has(origTask?.id)) {
                await deleteTask(origTask?.id);
              }
            }

            // Update or insert tasks
            for (let ti = 0; ti < (crewRow?.tasks || [])?.length; ti++) {
              const task = crewRow?.tasks?.[ti];
              if (!task?.nom?.trim()) continue;
              if (task?.id) {
                await updateTask(task?.id, { nom: task?.nom, pourcentage: task?.pourcentage ?? 0, sort_order: ti });
              } else {
                await addTask(crewRow?.id, { nom: task?.nom, pourcentage: task?.pourcentage ?? 0, sort_order: ti, previous_task_id: task?.previous_task_id || null });
              }
            }
          } else {
            // INSERT new crew row
            const orgId = organizationId;
            const { data: newCrewRow, error: crewInsertErr } = await supabase
              ?.from('daily_log_crew')
              ?.insert({
                daily_log_id: log?.id,
                subcontractor_id: crewRow?.subcontractorId,
                worker_count: crewRow?.workerCount || null,
                notes: crewRow?.notes || null,
              })
              ?.select()
              ?.single();
            if (crewInsertErr) throw crewInsertErr;

            // Insert tasks for new crew row
            for (let ti = 0; ti < (crewRow?.tasks || [])?.length; ti++) {
              const task = crewRow?.tasks?.[ti];
              if (!task?.nom?.trim()) continue;
              await addTask(newCrewRow?.id, { nom: task?.nom, pourcentage: task?.pourcentage ?? 0, sort_order: ti, previous_task_id: task?.previous_task_id || null });
            }
          }
        }
      } else {
        // ── CREATE new log ──
        const crewPayload = crew?.filter((c) => c?.subcontractorId);
        const { data: newLog, error: createErr } = await create({
          projectId,
          organizationId,
          logDate: form?.logDate,
          weatherCondition: form?.weatherCondition || null,
          weatherTemperature: form?.weatherTemperature !== '' ? parseFloat(form?.weatherTemperature) : null,
          workPerformed: form?.workPerformed,
          notes: form?.notes || null,
          materialsDelivered: form?.materialsDelivered || null,
          issueType: form?.issueType || null,
          issueDescription: form?.issueDescription || null,
          crew: crewPayload,
          photos: newPhotos?.map((p) => ({ file: p?.file, caption: p?.caption })),
          userId,
        });
        if (createErr) {
          if (createErr?.code === '23505') {
            setDateError(t('dailyLog.modal.dateAlreadyExists'));
            setSaving(false);
            return;
          }
          throw createErr;
        }

        // Insert tasks for newly created crew rows
        // We need to re-fetch the crew rows to get their IDs
        if (newLog && crewPayload?.some((c) => (c?.tasks || [])?.length > 0)) {
          const { data: insertedCrew } = await supabase
            ?.from('daily_log_crew')
            ?.select('id, subcontractor_id')
            ?.eq('daily_log_id', newLog?.id);

          for (let ci = 0; ci < crewPayload?.length; ci++) {
            const crewRow = crewPayload?.[ci];
            if (!(crewRow?.tasks?.length > 0)) continue;
            const matchedRow = insertedCrew?.find((r) => r?.subcontractor_id === crewRow?.subcontractorId);
            if (!matchedRow) continue;
            for (let ti = 0; ti < crewRow?.tasks?.length; ti++) {
              const task = crewRow?.tasks?.[ti];
              if (!task?.nom?.trim()) continue;
              await addTask(matchedRow?.id, { nom: task?.nom, pourcentage: task?.pourcentage ?? 0, sort_order: ti, previous_task_id: task?.previous_task_id || null });
            }
          }
        }
      }

      onSaved?.();
    } catch (err) {
      console.error('Save daily log error:', err);
      setError(err?.message || 'Failed to save.');
    } finally {
      setSaving(false);
      setCompressing(false);
      setUploadingCount(0);
    }
  };

  // ── Preview helpers ──
  const statusLabel = (status) => {
    if (status === 'approved') return t('dailyLog.status.draft') === 'Brouillon' ? 'Approuvé' : 'Approved'; // use lang
    if (status === 'submitted') return lang === 'fr' ? 'Soumis' : 'Submitted';
    return lang === 'fr' ? 'Brouillon' : 'Draft';
  };

  const approvalStatusLabel = (status) => {
    if (status === 'approved') return lang === 'fr' ? 'Approuvé' : 'Approved';
    if (status === 'submitted') return lang === 'fr' ? 'Soumis' : 'Submitted';
    return lang === 'fr' ? 'Brouillon' : 'Draft';
  };

  const weatherIcon = (condition) => WEATHER_ICONS?.[condition] || '';

  // Card preview values
  // Reperes sous le champ Date, tires de la liste deja chargee (aucune requete).
  const lastLogDate = (logs || [])?.reduce((latest, other) => (other?.log_date && (!latest || other?.log_date > latest) ? other?.log_date : latest), null);
  const missingDates = missingWeekdays((logs || [])?.map((other) => other?.log_date), todayStr());

  const previewCard1 = log
    ? `${formatDateShort(log?.log_date, lang)} · ${approvalStatusLabel(log?.approval_status)}`
    : formatDateShort(form?.logDate, lang);

  const previewCard2 = (log?.weather_condition || form?.weatherCondition) && (log?.weather_temperature != null || form?.weatherTemperature !== '')
    ? `${weatherIcon(log?.weather_condition || form?.weatherCondition)} ${log?.weather_condition || form?.weatherCondition} · ${log?.weather_temperature ?? form?.weatherTemperature}°C`
    : (log?.weather_condition || form?.weatherCondition)
      ? `${weatherIcon(log?.weather_condition || form?.weatherCondition)} ${log?.weather_condition || form?.weatherCondition}`
      : '—';

  const crewData = log?.crew || crew?.map((c) => ({
    subcontractor: projectIntervenants?.find((s) => s?.id === c?.subcontractorId),
    worker_count: c?.workerCount,
    tasks: c?.tasks,
  }));
  const subcount = crewData?.length || 0;
  const workercount = crewData?.reduce((sum, c) => sum + (c?.worker_count || 0), 0) || 0;
  const taskcount = crewData?.reduce((sum, c) => sum + (c?.tasks?.length || 0), 0) || 0;
  const previewCard3 = subcount === 0
    ? t('dailyLog.preview.noSubcontractor')
    : `${subcount} ${t('dailyLog.preview.subcontractors')} · ${workercount} ${t('dailyLog.preview.workers')} · ${taskcount} ${t('dailyLog.preview.tasks')}`;

  const matVal = log?.materials_delivered || form?.materialsDelivered || '';
  const previewCard4 = matVal
    ? matVal?.slice(0, 60) + (matVal?.length > 60 ? '…' : '')
    : '—';

  const issueVal = log?.issue_type || form?.issueType || '';
  const issueDescVal = log?.issue_description || form?.issueDescription || '';
  const previewCard5 = issueVal
    ? `${issueVal}${issueDescVal ? ' · ' + issueDescVal?.slice(0, 40) + (issueDescVal?.length > 40 ? '…' : '') : ''}`
    : t('dailyLog.preview.noIssue');

  const notesVal = log?.notes || form?.notes || '';
  const previewCard6 = notesVal
    ? notesVal?.slice(0, 60) + (notesVal?.length > 60 ? '…' : '')
    : '—';

  const photocount = (log?.daily_log_photos?.length || 0) + (isEdit ? 0 : newPhotos?.length);
  const previewCard7 = photocount === 0
    ? t('dailyLog.preview.noPhoto')
    : `${photocount} ${photocount > 1 ? t('dailyLog.preview.subcontractors')?.replace('sous-traitants', 'photos')?.replace('subcontractors', 'photos') : 'photo'}`;

  // Simpler photo preview
  const allPhotoCount = existingPhotos?.length + newPhotos?.length;
  const previewPhotos = allPhotoCount === 0
    ? t('dailyLog.preview.noPhoto')
    : `${allPhotoCount} photo${allPhotoCount > 1 ? 's' : ''}`;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">
            {isEdit ? t('dailyLog.modal.titleEdit') : t('dailyLog.modal.titleCreate')}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <Icon name="X" size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="max-h-[calc(100vh-160px)] overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <Icon name="AlertCircle" size={16} />
              {error}
            </div>
          )}

          {/* Card 1 — General Info */}
          <SectionCard
            icon={<ClipboardList size={20} />}
            title={t('dailyLog.sections.generalInfo')}
            subtitle=""
            preview={previewCard1}
            defaultExpanded={false}
          >
            {/* Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('dailyLog.modal.date')} *</label>
              <input
                type="date"
                value={form?.logDate}
                max={todayStr()}
                onChange={(e) => handleFormChange('logDate', e?.target?.value)}
                disabled={dateLocked}
                className={`w-full text-sm border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/20 ${dateLocked ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'border-gray-300'} ${dateError ? 'border-red-400' : ''}`}
              />
              {dateError && <p className="text-xs text-red-600 mt-1">{dateError}</p>}
              {dateLocked && <p className="text-xs text-gray-500 mt-1">{t('dailyLog.modal.dateLockedApproved')}</p>}
              {/* Reperes pour completer les journaux manquants, tires de la liste deja chargee. */}
              <p className="text-xs text-gray-500 mt-2">
                {lastLogDate
                  ? t('dailyLog.modal.lastLog')?.replace('{date}', formatDateShort(lastLogDate, lang))
                  : t('dailyLog.modal.noLogYet')}
              </p>
              {missingDates?.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {t('dailyLog.modal.missingDays')?.replace(
                    '{dates}',
                    `${missingDates?.slice(0, 5)?.map((d) => formatDateShort(d, lang))?.join(', ')}${missingDates?.length > 5 ? ' …' : ''}`
                  )}
                </p>
              )}
            </div>
          </SectionCard>

          {/* Card 2 — Site Conditions */}
          <SectionCard
            icon={<CloudSun size={20} />}
            title={t('dailyLog.sections.siteConditions')}
            subtitle=""
            preview={previewCard2}
            defaultExpanded={false}
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('dailyLog.weather.condition')}</label>
                <select
                  value={form?.weatherCondition}
                  onChange={(e) => handleFormChange('weatherCondition', e?.target?.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
                >
                  <option value="">{t('dailyLog.weather.conditionPlaceholder')}</option>
                  {WEATHER_CONDITIONS?.map((c) => (
                    <option key={c} value={c}>{t(`dailyLog.weather.${c}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('dailyLog.weather.temperature')}</label>
                <input
                  type="number"
                  step="0.1"
                  value={form?.weatherTemperature}
                  onChange={(e) => handleFormChange('weatherTemperature', e?.target?.value)}
                  placeholder={t('dailyLog.weather.temperature')}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
          </SectionCard>

          {/* Card 3 — Crew on Site */}
          <SectionCard
            icon={<Users size={20} />}
            title={t('dailyLog.sections.crew')}
            subtitle=""
            preview={previewCard3}
            defaultExpanded={true}
          >
            <div>
              {/* Auto-populate info banner */}
              {carriedCrew && carriedCrew?.length > 0 && !isEdit && (
                <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                  <Icon name="Info" size={14} className="text-blue-500 flex-shrink-0" />
                  <p className="text-xs text-blue-700">
                    {(() => {
                      const subCount = carriedCrew?.length;
                      const taskCount = carriedCrew?.reduce((sum, c) => sum + (c?.tasks?.length || 0), 0);
                      return lang === 'fr'
                        ? `${subCount} sous-traitant${subCount > 1 ? 's' : ''} et ${taskCount} tâche${taskCount > 1 ? 's' : ''} ${t('dailyLog.carriedFromPrevious')}`
                        : `${subCount} subcontractor${subCount > 1 ? 's' : ''} and ${taskCount} task${taskCount > 1 ? 's' : ''} ${t('dailyLog.carriedFromPrevious')}`;
                    })()}
                  </p>
                </div>
              )}
              {/* Empty project intervenants state */}
              {projectIntervenants !== null && projectIntervenants?.length === 0 && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between gap-3">
                  <p className="text-xs text-amber-700">{t('dailyLog.picker.empty')}</p>
                  <button
                    type="button"
                    onClick={() => {
                      onClose?.();
                      // Navigate to project directory tab
                      window.location.href = window.location?.href?.replace('/daily-log', '/directory');
                    }}
                    className="text-xs font-medium text-orange-600 hover:text-orange-700 whitespace-nowrap"
                  >
                    {t('dailyLog.picker.addToRepertoire')}
                  </button>
                </div>
              )}
              {crew?.length === 0 ? (
                <p className="text-sm text-gray-400 italic py-2">{t('dailyLog.crew.emptyState')}</p>
              ) : (
                <div className="space-y-3">
                  {crew?.map((row, idx) => (
                    <div key={row?.id || `new_${idx}`} data-crew-index={idx}>
                      <CrewEditCard
                        row={row}
                        index={idx}
                        subcontractors={subcontractors}
                        projectIntervenants={projectIntervenants}
                        onChange={updateCrewRow}
                        onRemove={removeCrewRow}
                        onAddToRepertoire={() => {
                          onClose?.();
                          window.location.href = window.location?.href?.replace('/daily-log', '/directory');
                        }}
                        t={t}
                        lang={lang}
                        hasValidationError={crewValidationErrors?.some((e) => e?.idx === idx)}
                      />
                    </div>
                  ))}
                </div>
              )}
              {/* ONE add-subcontractor button at the bottom of the crew section */}
              <button
                type="button"
                onClick={addCrewRow}
                className="mt-3 text-xs font-medium text-orange-600 hover:text-orange-700 flex items-center gap-1"
              >
                <Icon name="Plus" size={14} />
                {t('dailyLog.crew.addSubcontractor')}
              </button>
            </div>
          </SectionCard>

          {/* Card 4 — Materials Delivered (was Card 5) */}
          <SectionCard
            icon={<Package size={20} />}
            title={t('dailyLog.sections.materialsDelivered')}
            subtitle=""
            preview={previewCard4}
            defaultExpanded={false}
          >
            <div>
              <textarea
                value={form?.materialsDelivered || ''}
                onChange={(e) => handleFormChange('materialsDelivered', e?.target?.value)}
                placeholder={lang === 'fr' ? 'Listez les matériaux livrés sur le chantier aujourd\'hui...' : 'List materials delivered to site today...'}
                rows={3}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              />
            </div>
          </SectionCard>

          {/* Card 5 — Issues (was Card 6) */}
          <SectionCard
            icon={<AlertTriangle size={20} />}
            title={t('dailyLog.sections.issues')}
            subtitle=""
            preview={previewCard5}
            defaultExpanded={false}
          >
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  {lang === 'fr' ? 'Type d\'enjeu' : 'Issue Type'}
                </label>
                <select
                  value={form?.issueType || ''}
                  onChange={(e) => handleFormChange('issueType', e?.target?.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white"
                >
                  <option value="">{lang === 'fr' ? 'Sélectionner un type' : 'Select issue type'}</option>
                  <option value="incident">{lang === 'fr' ? 'Incident' : 'Incident'}</option>
                  <option value="retard">{lang === 'fr' ? 'Retard' : 'Delay'}</option>
                  <option value="qualite">{lang === 'fr' ? 'Qualité' : 'Quality'}</option>
                  <option value="securite">{lang === 'fr' ? 'Sécurité' : 'Safety'}</option>
                  <option value="autre">{lang === 'fr' ? 'Autre' : 'Other'}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  {lang === 'fr' ? 'Description de l\'enjeu' : 'Issue Description'}
                </label>
                <textarea
                  value={form?.issueDescription || ''}
                  onChange={(e) => handleFormChange('issueDescription', e?.target?.value)}
                  placeholder={lang === 'fr' ? 'Décrivez l\'enjeu...' : 'Describe the issue...'}
                  rows={3}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                />
              </div>
            </div>
          </SectionCard>

          {/* Card 6 — General Notes (was Card 7) */}
          <SectionCard
            icon={<StickyNote size={20} />}
            title={t('dailyLog.sections.notes')}
            subtitle=""
            preview={previewCard6}
            defaultExpanded={false}
          >
            <div>
              <textarea
                value={form?.notes}
                onChange={(e) => handleFormChange('notes', e?.target?.value)}
                placeholder={t('dailyLog.notesPlaceholder')}
                rows={3}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
              />
            </div>
          </SectionCard>

          {/* Card 7 — Photos (was Card 8) */}
          <SectionCard
            icon={<Camera size={20} />}
            title={t('dailyLog.sections.photos')}
            subtitle=""
            preview={previewPhotos}
            defaultExpanded={false}
          >
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-400">{totalPhotos}/20</span>
              </div>

              {totalPhotos < 20 && (
                <div
                  ref={dropzoneRef}
                  onDragOver={(e) => { e?.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef?.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors mb-3 ${isDragging ? 'border-orange-400 bg-orange-50' : 'border-gray-300 hover:border-orange-300 hover:bg-orange-50/30'}`}
                >
                  <Icon name="Upload" size={24} className="text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 font-medium">{t('dailyLog.photos.dropzone')}</p>
                  <p className="text-xs text-gray-400 mt-1">{t('dailyLog.photos.limit')}</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    multiple
                    className="hidden"
                    onChange={handleFileInput}
                  />
                </div>
              )}

              {/* Compression / upload spinner */}
              {compressing && (
                <div className="flex items-center gap-2 text-sm text-orange-600 font-medium mb-2">
                  <Icon name="Loader2" size={15} className="animate-spin" />
                  <span>
                    {uploadingCount > 0
                      ? t('dailyLog.photos.uploadingCount')?.replace('{count}', uploadingCount)
                      : t('dailyLog.photos.compressing')}
                  </span>
                </div>
              )}

              {(existingPhotos?.length > 0 || newPhotos?.length > 0) && (
                <div className="grid grid-cols-4 gap-3">
                  {existingPhotos?.map((photo) => (
                    <PhotoItem
                      key={photo?.id}
                      photo={photo}
                      onRemove={removeExistingPhoto}
                      onCaptionChange={updateExistingCaption}
                      t={t}
                      isExisting={true}
                    />
                  ))}
                  {newPhotos?.map((photo) => (
                    <PhotoItem
                      key={photo?.tempId}
                      photo={photo}
                      onRemove={removeNewPhoto}
                      onCaptionChange={updateNewCaption}
                      t={t}
                      isExisting={false}
                    />
                  ))}
                </div>
              )}
            </div>
          </SectionCard>
        </div>

        {/* Sticky Footer */}
        <div className="sticky bottom-0 z-10 flex items-center justify-end gap-3 px-6 py-3 border-t border-gray-200 bg-white rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {t('dailyLog.modal.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !!dateError}
            className="px-5 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Icon name="Loader2" size={15} className="animate-spin" />}
            {compressing ? t('dailyLog.photos.compressing') : saving ? t('dailyLog.modal.saving') : t('dailyLog.modal.save')}
          </button>
        </div>
      </div>

      {/* Changement de date, equipe deja modifiee : on demande avant de remplacer. */}
      <ConfirmDialog
        isOpen={!!pendingPrefill}
        title={t('dailyLog.modal.date')}
        message={t('dailyLog.modal.datePrefillReplace')}
        confirmLabel={t('dailyLog.modal.datePrefillReplaceConfirm')}
        cancelLabel={t('dailyLog.modal.cancel')}
        onConfirm={() => {
          applyPrefill(pendingPrefill?.crew || []);
          setPendingPrefill(null);
        }}
        onCancel={() => {
          // L'equipe saisie reste, et la date revient a celle d'avant : sinon ses
          // taches reportees pointeraient vers un journal d'une autre date.
          const previousDate = pendingPrefill?.previousDate;
          setPendingPrefill(null);
          setForm((prev) => ({ ...prev, logDate: previousDate }));
          setDateError(dateProblem(previousDate, []));
        }}
      />

      {/* Validation Modal */}
      <ValidationModal
        isOpen={validationModal?.open}
        title={t('dailyLog.validation.errorTitle')}
        messages={validationModal?.messages || []}
        closeLabel={t('dailyLog.validation.close')}
        goToTaskLabel={t('dailyLog.validation.goToTask')}
        onClose={() => setValidationModal((prev) => ({ ...prev, open: false }))}
        onGoToTask={() => {
          setValidationModal((prev) => ({ ...prev, open: false }));
          const firstError = validationModal?.invalidBlocks?.[0];
          if (firstError == null) return;
          const cardEl = document.querySelector(`[data-crew-index="${firstError?.idx}"]`);
          if (cardEl) {
            cardEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // If the card has no tasks, auto-trigger the add-task button
            if (firstError?.reason === 'no_task') {
              const addTaskBtn = cardEl?.querySelector('[data-add-task-btn]');
              if (addTaskBtn) {
                setTimeout(() => {
                  addTaskBtn?.click();
                  addTaskBtn?.focus();
                }, 350);
              }
            } else {
              // Focus the add-task button for invalid percentage case
              const addTaskBtn = cardEl?.querySelector('[data-add-task-btn]');
              setTimeout(() => addTaskBtn?.focus(), 350);
            }
          }
        }}
      />
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────

const DailyLogPage = () => {
  const { id: projectId } = useParams();
  const { t, language } = useTranslation();
  const { user, userProfile } = useAuth();

  const [project, setProject] = useState(null);
  const [logs, setLogs] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessStatus, setAccessStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [viewingLog, setViewingLog] = useState(null);
  const [deleteLog, setDeleteLog] = useState(null);
  const [toast, setToast] = useState(null);
  const [initialCrewForModal, setInitialCrewForModal] = useState(null);

  // Sort state
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  // Filter state — dateRangeOption drives dateFrom/dateTo; 'custom' exposes manual inputs
  const [dateRangeOption, setDateRangeOption] = useState('30');
  const [filters, setFilters] = useState(() => {
    const r = computeDateRange('30');
    return { dateFrom: r?.dateFrom, dateTo: r?.dateTo, weather: '', author: '', approval: '' };
  });

  // Checkbox / bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    if (projectId && user?.id) {
      loadAll();
    }
  }, [projectId, user?.id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const projectData = await projectService?.getById(projectId);
      setProject(projectData);

      const userId = user?.id;
      const canViewResult = canSeeAllProjects(userProfile) || canView(projectData, userId);
      const canEditResult = canEditProject(projectData, userProfile) || canEdit(projectData, userId);

      if (!canViewResult) {
        setAccessStatus('denied');
        setLoading(false);
        return;
      }
      setAccessStatus(canEditResult ? 'edit' : 'view');

      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('organization_id')
        ?.eq('id', userId)
        ?.single();
      setOrganizationId(profile?.organization_id || null);

      const { data: logsData } = await getByProject(projectId);
      setLogs(logsData || []);

      const { data: subs } = await supabase
        ?.from('subcontractors')
        ?.select('id, company_name')
        ?.order('company_name', { ascending: true });
      setSubcontractors(subs || []);
    } catch (err) {
      console.error('DailyLogPage loadAll error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNewLog = async () => {
    setEditingLog(null);
    setInitialCrewForModal(null);

    // Auto-populate from previous log
    const today = todayStr();
    try {
      const { data: prevLog } = await getPreviousLogWithCrew(projectId, today);
      if (prevLog && (prevLog?.crew || [])?.length > 0) {
        // Build initialCrew: only crew rows that have at least one incomplete task
        const initialCrew = carriedCrewFrom(prevLog);

        if (initialCrew?.length > 0) {
          setInitialCrewForModal(initialCrew);
        }
      }
    } catch (err) {
      console.warn('Auto-populate failed, opening empty modal:', err);
    }

    setShowModal(true);
  };

  // Row click → open View modal
  const handleRowClick = (log) => {
    setViewingLog(log);
  };

  // View action from 3-dots
  const handleView = (log) => {
    setViewingLog(log);
  };

  // Edit action from 3-dots or from View modal
  const handleEdit = (log) => {
    setViewingLog(null);
    setEditingLog(log);
    setShowModal(true);
  };

  const handleDeleteRequest = (log) => {
    setDeleteLog(log);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteLog) return;
    try {
      const { error } = await supabase?.from('daily_logs')?.delete()?.eq('id', deleteLog?.id);
      if (error) throw error;
      showToast('Log deleted.');
      setDeleteLog(null);
      const { data: logsData } = await getByProject(projectId);
      setLogs(logsData || []);
    } catch (err) {
      console.error('Delete error:', err);
      showToast('Failed to delete log.', 'error');
      setDeleteLog(null);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingLog(null);
    setInitialCrewForModal(null);
  };

  const handleSaved = async () => {
    setShowModal(false);
    setEditingLog(null);
    setInitialCrewForModal(null);
    showToast(editingLog ? 'Log updated successfully.' : 'Log created successfully.');
    const { data: logsData } = await getByProject(projectId);
    setLogs(logsData || []);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setDateRangeOption('30');
    const range = computeDateRange('30');
    setFilters({ dateFrom: range?.dateFrom, dateTo: range?.dateTo, weather: '', author: '', approval: '' });
  };

  const handleDateRangeChange = (v) => {
    setDateRangeOption(v);
    if (v !== 'custom') {
      const { dateFrom, dateTo } = computeDateRange(v);
      setFilters((prev) => ({ ...prev, dateFrom, dateTo }));
    } else {
      setFilters((prev) => ({ ...prev, dateFrom: '', dateTo: '' }));
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // ── Apply filters ─────────────────────────────────────────────────────────────
  const filteredLogs = useMemo(() => {
    return logs?.filter((log) => {
      if (filters?.dateFrom && log?.log_date < filters?.dateFrom) return false;
      if (filters?.dateTo && log?.log_date > filters?.dateTo) return false;
      if (filters?.weather && log?.weather_condition !== filters?.weather) return false;
      if (filters?.author && log?.creator?.id !== filters?.author) return false;
      if (filters?.approval && (log?.approval_status || 'submitted') !== filters?.approval) return false;
      return true;
    });
  }, [logs, filters]);

  // ── Apply sort ────────────────────────────────────────────────────────────────
  const sortedLogs = useMemo(() => {
    const sorted = [...filteredLogs];
    sorted?.sort((a, b) => {
      let aVal, bVal;
      switch (sortConfig?.key) {
        case 'date':
          aVal = a?.log_date || '';
          bVal = b?.log_date || '';
          break;
        case 'weather':
          aVal = a?.weather_condition || '';
          bVal = b?.weather_condition || '';
          break;
        case 'team':
          aVal = (a?.crew || [])?.length;
          bVal = (b?.crew || [])?.length;
          break;
        case 'photos':
          aVal = (a?.daily_log_photos || [])?.length;
          bVal = (b?.daily_log_photos || [])?.length;
          break;
        case 'author':
          aVal = a?.creator?.full_name || a?.creator?.email || '';
          bVal = b?.creator?.full_name || b?.creator?.email || '';
          break;
        default:
          aVal = a?.log_date || '';
          bVal = b?.log_date || '';
      }
      if (aVal < bVal) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredLogs, sortConfig]);

  // ── Checkbox handlers ─────────────────────────────────────────────────────────
  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id);
      else next?.add(id);
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    if (sortedLogs?.every((l) => selectedIds?.has(l?.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedLogs.map((l) => l.id)));
    }
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  // ── Export single PDF ─────────────────────────────────────────────────────────
  const [pdfExporting, setPdfExporting] = useState(false);

  const handleExportSinglePDF = async (log) => {
    setPdfExporting(true);
    showToast(language === 'fr' ? 'Génération du PDF...' : 'Generating PDF...', 'success');
    try {
      const { data: org } = await supabase
        ?.from('organizations')
        ?.select('id, name, logo_url, tagline')
        ?.eq('id', organizationId)
        ?.single();
      const logoBase64 = await getOrgLogoBase64Cached(supabase, org?.logo_url);
      const orgName = org?.name || 'LHIAM Construction';
      await downloadSinglePDF(log, project, t, language, logoBase64, orgName);
      showToast(language === 'fr' ? 'PDF prêt !' : 'PDF ready!', 'success');
    } catch (err) {
      console.error('PDF export error:', err);
      showToast(t('dailyLog.bulk.error'), 'error');
    } finally {
      setPdfExporting(false);
    }
  };

  // ── Bulk approve ──────────────────────────────────────────────────────────────
  // daily_logs ne connait que 'submitted' et 'approved' (CHECK en base) : il
  // n'y a pas de brouillon. « Ignore » signifie donc « deja approuve ».
  const handleBulkApprove = async () => {
    const selectedLogs = sortedLogs?.filter((l) => selectedIds?.has(l?.id));
    if (!selectedLogs || selectedLogs?.length === 0) return;

    const toApprove = selectedLogs?.filter((l) => (l?.approval_status || 'submitted') === 'submitted');
    const skipped = selectedLogs?.length - toApprove?.length;

    setBulkApproving(true);
    try {
      // allSettled : un journal en echec ne doit pas annuler les autres.
      const results = await Promise.allSettled(
        toApprove?.map((l) => approve(l?.id, user?.id))
      );
      // Une promesse tenue peut quand meme porter une erreur Supabase : le
      // service rend { data, error } au lieu de lever.
      const approved = results?.filter(
        (r) => r?.status === 'fulfilled' && !r?.value?.error
      )?.length;
      const failed = results?.length - approved;

      const { data: logsData } = await getByProject(projectId);
      setLogs(logsData || []);
      setSelectedIds(new Set());

      const message = failed > 0
        ? `${t('dailyLog.bulk.approveResult', { approved, skipped })} · ${t('dailyLog.bulk.approveFailed', { failed })}`
        : t('dailyLog.bulk.approveResult', { approved, skipped });
      showToast(message, failed > 0 ? 'error' : 'success');
    } catch (err) {
      console.error('Bulk approve error:', err);
      showToast(t('dailyLog.bulk.error'), 'error');
    } finally {
      setBulkApproving(false);
    }
  };

  // ── Bulk download ─────────────────────────────────────────────────────────────
  const handleBulkDownload = async () => {
    const selectedLogs = sortedLogs?.filter((l) => selectedIds?.has(l?.id));
    if (selectedLogs?.length === 0) return;

    setBulkDownloading(true);
    try {
      // Fetch org + logo once for all PDFs in the batch
      const { data: org } = await supabase
        ?.from('organizations')
        ?.select('id, name, logo_url, tagline')
        ?.eq('id', organizationId)
        ?.single();
      const logoBase64 = await getOrgLogoBase64Cached(supabase, org?.logo_url);
      const orgName = org?.name || 'LHIAM Construction';

      if (selectedLogs?.length === 1) {
        await downloadSinglePDF(selectedLogs?.[0], project, t, language, logoBase64, orgName);
      } else {
        // Multiple: bundle into ZIP
        const JSZip = (await import('jszip'))?.default;
        const zip = new JSZip();
        const projectNum = project?.projectNumber || 'LOG';

        for (const log of selectedLogs) {
          const doc = await generateDailyLogPDF(log, project, t, language, logoBase64, orgName);
          const pdfBytes = doc?.output('arraybuffer');
          const dateStr = log?.log_date || 'unknown';
          zip?.file(`daily-log-${projectNum}-${dateStr}.pdf`, pdfBytes);
        }

        const timestamp = new Date()?.toISOString()?.slice(0, 16)?.replace('T', '-')?.replace(':', '');
        const zipBlob = await zip?.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `daily-logs-${projectNum}-${timestamp}.zip`;
        document.body?.appendChild(a);
        a?.click();
        document.body?.removeChild(a);
        URL.revokeObjectURL(url);
      }

      showToast(t('dailyLog.bulk.success')?.replace('{count}', selectedLogs?.length));
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Bulk download error:', err);
      showToast(t('dailyLog.bulk.error'), 'error');
    } finally {
      setBulkDownloading(false);
    }
  };

  // Dates that already have logs (for duplicate prevention)
  const existingDates = logs?.map((l) => l?.log_date);

  // ── KPI calculations ──────────────────────────────────────────────────────────
  const totalLogs = logs?.length || 0;

  const awaitingApproval = logs?.filter((l) => (l?.approval_status || 'submitted') === 'submitted')?.length || 0;

  const missingLogs = (() => {
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    const logDates = new Set(logs?.map((l) => l?.log_date));
    let weekdayCount = 0;
    let loggedCount = 0;
    for (let i = 0; i < 30; i++) {
      const d = new Date(today);
      d?.setDate(today?.getDate() - i);
      const dow = d?.getDay();
      if (dow === 0 || dow === 6) continue;
      weekdayCount++;
      const dateStr = d?.toISOString()?.split('T')?.[0];
      if (logDates?.has(dateStr)) loggedCount++;
    }
    return Math.max(0, weekdayCount - loggedCount);
  })();

  const missingLogsColor = missingLogs === 0 ? 'text-green-600' : missingLogs <= 3 ? 'text-orange-500' : 'text-red-600';

  // ── Approval handlers ─────────────────────────────────────────────────────────
  const handleApprove = async (log) => {
    try {
      const { data: updated, error } = await approve(log?.id, user?.id);
      if (error) throw error;
      if (updated) {
        setLogs((prev) => prev?.map((l) => l?.id === updated?.id ? updated : l));
        if (viewingLog?.id === updated?.id) setViewingLog(updated);
      }
      showToast(t('dailyLog.approval.toastApproved'));
    } catch (err) {
      console.error('Approve error:', err);
      showToast(t('dailyLog.bulk.error'), 'error');
    }
  };

  const handleUnapprove = async (log) => {
    try {
      const { data: updated, error } = await unapprove(log?.id);
      if (error) throw error;
      if (updated) {
        setLogs((prev) => prev?.map((l) => l?.id === updated?.id ? updated : l));
        if (viewingLog?.id === updated?.id) setViewingLog(updated);
      }
      showToast(t('dailyLog.approval.toastUnapproved'));
    } catch (err) {
      console.error('Unapprove error:', err);
      showToast(t('dailyLog.bulk.error'), 'error');
    }
  };

  // ── Distinct authors for filter dropdown ──────────────────────────────────────
  const authors = useMemo(() => {
    const seen = new Map();
    logs?.forEach((log) => {
      if (log?.creator?.id && !seen?.has(log?.creator?.id)) {
        seen?.set(log?.creator?.id, {
          id: log?.creator?.id,
          name: log?.creator?.full_name || log?.creator?.email || log?.creator?.id,
        });
      }
    });
    return Array.from(seen?.values());
  }, [logs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Icon name="Loader2" size={40} className="animate-spin text-orange-500" />
      </div>
    );
  }

  if (accessStatus === 'denied') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Icon name="Lock" size={48} className="text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">{t('dailyLog.accessDenied')}</p>
        </div>
      </div>
    );
  }

  const userCanEdit = accessStatus === 'edit';
  const userCanApprove = canApprove(project, user, userProfile);
  const selectedCount = selectedIds?.size;

  return (
    <>
      <div className="pt-0 pb-0 px-0 h-full flex flex-col">
        {/* Header Block */}
        <div className="mb-6">
          <div className="flex items-start justify-between gap-6">
            {/* Left Side: Icon + Title + Project Info + Subtitle */}
            <div className="flex-shrink-0">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="ClipboardList" size={20} className="text-primary" />
                <h1 className="text-lg font-semibold text-gray-900">
                  {t('dailyLog.title')}
                </h1>
              </div>
              <div className="flex items-center gap-1 mb-1">
                <span className="text-sm text-primary font-normal">{project?.projectNumber || ''}</span>
                {project?.projectNumber && project?.name && (
                  <span className="text-sm text-primary font-normal">-</span>
                )}
                <span className="text-sm text-primary font-normal">{project?.name || t('common.loading')}</span>
              </div>
              <p className="text-xs text-gray-500">{t('dailyLog.subtitle')}</p>
            </div>

            {/* Right Side: KPI Cards */}
            <div className="flex-1 flex items-center justify-end gap-2">
              <StatsCard
                title={t('dailyLog.kpi.totalLogs')}
                value={totalLogs}
                iconName="ClipboardList"
                iconColor="text-blue-500"
                valueColor="text-blue-500"
                titleWeight="font-medium"
                sublabel=""
              />
              <StatsCard
                title={t('dailyLog.kpi.awaitingApproval')}
                value={awaitingApproval}
                iconName="Clock"
                iconColor={awaitingApproval > 0 ? 'text-orange-500' : 'text-gray-400'}
                valueColor={awaitingApproval > 0 ? 'text-orange-500' : 'text-gray-400'}
                titleWeight="font-medium"
                sublabel=""
              />
              <StatsCard
                title={t('dailyLog.kpi.missingLogs')}
                value={missingLogs}
                iconName="AlertCircle"
                iconColor={missingLogs === 0 ? 'text-green-500' : missingLogs <= 3 ? 'text-orange-500' : 'text-red-500'}
                valueColor={missingLogsColor}
                titleWeight="font-medium"
                sublabel=""
              />
            </div>
          </div>
        </div>

        {/* Toolbar: filter + new log button — same row, same height */}
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <DateRangeFilter
            dateRangeOption={dateRangeOption}
            onDateRangeChange={handleDateRangeChange}
            customFrom={filters?.dateFrom}
            customTo={filters?.dateTo}
            onCustomFromChange={(v) => setFilters((prev) => ({ ...prev, dateFrom: v }))}
            onCustomToChange={(v) => setFilters((prev) => ({ ...prev, dateTo: v }))}
            filteredCount={filteredLogs?.length || 0}
            lang={language}
            t={t}
          />
          {userCanEdit && (
            <button
              onClick={handleNewLog}
              className="inline-flex items-center gap-1.5 px-3 h-7 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-md transition-colors shadow-sm whitespace-nowrap"
            >
              <Icon name="Plus" size={13} />
              {t('dailyLog.newLog')}
            </button>
          )}
        </div>

        {/* Table */}
        <DailyLogTable
          logs={sortedLogs}
          sortConfig={sortConfig}
          onSort={handleSort}
          onRowClick={handleRowClick}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteRequest}
          userCanEdit={userCanEdit}
          userCanApprove={userCanApprove}
          onApprove={handleApprove}
          onUnapprove={handleUnapprove}
          t={t}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelect}
          onToggleSelectAll={handleToggleSelectAll}
          lang={language}
        />
      </div>

      {/* View Modal */}
      {viewingLog && (
        <ViewModal
          log={viewingLog}
          project={project}
          userCanEdit={userCanEdit}
          onClose={() => setViewingLog(null)}
          onEdit={handleEdit}
          onExportPDF={handleExportSinglePDF}
          pdfExporting={pdfExporting}
          t={t}
        />
      )}

      {/* Edit Modal */}
      {showModal && (
        <DailyLogModal
          log={editingLog}
          projectId={projectId}
          organizationId={organizationId}
          userId={user?.id}
          subcontractors={subcontractors}
          existingDates={existingDates}
          logs={logs}
          onClose={handleModalClose}
          onSaved={handleSaved}
          t={t}
          lang={language}
          initialCrew={initialCrewForModal}
        />
      )}

      {/* Delete Confirm */}
      {deleteLog && (
        <ConfirmDialog
          isOpen={!!deleteLog}
          title={t('dailyLog.actions.delete')}
          message={`${formatDateShort(deleteLog?.log_date)} — ${t('common.delete')}?`}
          confirmVariant="danger"
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteLog(null)}
        />
      )}

      {/* Bulk Action Bar */}
      <BulkActionBar
        count={selectedCount}
        actions={[
          ...(userCanApprove ? [{
            key: 'approve',
            label: bulkApproving ? t('dailyLog.bulk.approving') : t('dailyLog.bulk.approve'),
            icon: 'CheckCircle2',
            variant: 'success',
            loading: bulkApproving,
            disabled: bulkApproving,
            onClick: handleBulkApprove,
          }] : []),
          {
            key: 'downloadPdf',
            label: bulkDownloading ? t('bulk.downloading') : t('bulk.downloadPdf'),
            icon: 'Download',
            variant: 'primary',
            loading: bulkDownloading,
            onClick: handleBulkDownload,
          },
        ]}
        onClear={handleClearSelection}
        clearLabel={t('bulk.clear')}
      />

      {/* Toast */}
      {toast && (
        <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />
      )}
    </>
  );
};

export default DailyLogPage;
